import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import PageConsensusBanner from '../components/PageConsensusBanner';
import BlindSpotsBanner from '../components/BlindSpotsBanner';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import {
  Star, ArrowLeft, CircleNotch, DownloadSimple, CaretUp, CaretDown,
  ArrowsOutLineHorizontal, Target, ListBullets, ChartBar, Clock,
} from '@phosphor-icons/react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useDrawsUpdated } from '../hooks/useDrawsUpdated';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const HMLBadge = ({ hml }) => {
  if (hml === 'H') return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-800" data-testid="hml-badge-h">H</span>;
  if (hml === 'M') return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800" data-testid="hml-badge-m">M</span>;
  return <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-800" data-testid="hml-badge-l">L</span>;
};

const ResizableHeader = ({ children, width, onResize, columnKey, onClick, className = '' }) => {
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const handleMouseDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    startXRef.current = e.clientX; startWidthRef.current = width;
    const move = (ev) => { onResize(columnKey, Math.max(35, startWidthRef.current + (ev.clientX - startXRef.current))); };
    const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
  };
  return (
    <th className={`relative ${className}`} style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }} onClick={onClick}>
      <div className="flex items-center justify-center gap-1 pr-2 font-bold">{children}</div>
      <div className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-blue-500/30 group flex items-center justify-center" onMouseDown={handleMouseDown} onClick={(e) => e.stopPropagation()}>
        <div className="w-0.5 h-4 bg-gray-300 group-hover:bg-blue-500 transition-colors" />
      </div>
    </th>
  );
};

const BallTable = ({ numbersData, data, sortConfig, onSort, colWidths, onResize }) => {
  const sorted = [...numbersData].sort((a, b) => {
    let aVal, bVal;
    const k = sortConfig.key;
    if (['number', 'times', 'avg', 'overall_pct', 'month_count', 'month_pct', 'month_diff',
         'y1_count', 'y2_count', 'y3_count', 'cy_count',
         'y1_pct', 'y2_pct', 'y3_pct', 'cy_pct',
         'pct_diff', 'avg_diff', 'pred_score'].includes(k)) {
      aVal = a[k] || 0; bVal = b[k] || 0;
    } else { aVal = 0; bVal = 0; }
    return sortConfig.dir === 'desc' ? bVal - aVal : aVal - bVal;
  });
  const topPredicted = new Set([...numbersData].sort((a, b) => (b.pred_score || 0) - (a.pred_score || 0)).slice(0, 5).map(n => n.number));


  const SortIcon = ({ k }) => {
    if (sortConfig.key !== k) return null;
    return sortConfig.dir === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;
  };

  const hCls = "px-1 py-2 text-[10px] uppercase tracking-wider text-gray-700 cursor-pointer hover:bg-gray-200 text-center";
  const { yr1, yr2, yr3, current_year: cy, current_month_abbr: mon } = data;

  return (
    <div className="overflow-x-auto" data-testid="number-appearance-table">
      <table className="w-full text-xs" style={{ tableLayout: 'fixed', minWidth: '1100px' }}>
        <thead className="bg-gray-100">
          <tr className="bg-gray-50 text-[9px] text-gray-400">
            <th style={{ width: `${colWidths.num}px` }}></th>
            <th style={{ width: `${colWidths.times}px` }}></th>
            <th style={{ width: `${colWidths.avg}px` }}></th>
            <th style={{ width: `${colWidths.pct}px` }}>{data.total_draws}</th>
            <th style={{ width: `${colWidths.hml}px` }}></th>
            <th style={{ width: `${colWidths.mon}px` }}>{data.total_month_draws}</th>
            <th style={{ width: `${colWidths.mpct}px` }}></th>
            <th style={{ width: `${colWidths.diff}px` }}></th>
            <th style={{ width: `${colWidths.yr}px` }}>{data.draws_per_year?.[String(cy)] || 0}</th>
            <th style={{ width: `${colWidths.yrp}px` }}></th>
            <th style={{ width: `${colWidths.pdiff}px` }}></th>
            <th style={{ width: `${colWidths.adiff}px` }}></th>
          </tr>
          <tr>
            <ResizableHeader width={colWidths.num} onResize={onResize} columnKey="num" onClick={() => onSort('number')} className={hCls}>
              # <SortIcon k="number" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.times} onResize={onResize} columnKey="times" onClick={() => onSort('times')} className={`${hCls} bg-gray-200`}>
              Times <SortIcon k="times" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.avg} onResize={onResize} columnKey="avg" onClick={() => onSort('avg')} className={hCls}>
              AVG <SortIcon k="avg" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.pct} onResize={onResize} columnKey="pct" onClick={() => onSort('overall_pct')} className={`${hCls} bg-gray-200`}>
              % <SortIcon k="overall_pct" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.hml} onResize={onResize} columnKey="hml" className={hCls}>
              HML
            </ResizableHeader>
            <ResizableHeader width={colWidths.mon} onResize={onResize} columnKey="mon" onClick={() => onSort('month_count')} className={`${hCls} bg-amber-100`}>
              {mon} <SortIcon k="month_count" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.mpct} onResize={onResize} columnKey="mpct" onClick={() => onSort('month_pct')} className={`${hCls} bg-amber-100`}>
              {mon}% <SortIcon k="month_pct" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.diff} onResize={onResize} columnKey="diff" onClick={() => onSort('month_diff')} className={`${hCls} bg-red-100`}>
              Diff <SortIcon k="month_diff" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.yr} onResize={onResize} columnKey="yr" onClick={() => onSort('cy_count')} className={`${hCls} bg-green-100`}>
              {cy} <SortIcon k="cy_count" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.yrp} onResize={onResize} columnKey="yrp" onClick={() => onSort('cy_pct')} className={`${hCls} bg-green-50`}>
              % <SortIcon k="cy_pct" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.pdiff} onResize={onResize} columnKey="pdiff" onClick={() => onSort('pct_diff')} className={`${hCls} bg-purple-100`}>
              %Diff <SortIcon k="pct_diff" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.adiff} onResize={onResize} columnKey="adiff" onClick={() => onSort('avg_diff')} className={`${hCls} bg-purple-100`}>
              AvgDf <SortIcon k="avg_diff" />
            </ResizableHeader>
          </tr>
        </thead>
        <tbody>
          { sorted.map((row, idx) => (
            <tr key={row.number} className={topPredicted.has(row.number) ? 'bg-blue-100' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} style={topPredicted.has(row.number) ? {borderLeft: '4px solid #2563eb', backgroundColor: 'rgba(37, 99, 235, 0.18)'} : {}} data-testid={`row-${row.number}`}>
              <td className="px-1 py-1 border-b border-gray-100 font-mono font-bold text-center">{row.number}</td>
              <td className="px-1 py-1 border-b border-gray-100 font-mono text-center font-bold bg-gray-50">{row.times}</td>
              <td className="px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px]">{row.avg}</td>
              <td className="px-1 py-1 border-b border-gray-100 font-mono text-center bg-gray-50 text-[10px]">
                {(row.overall_pct * 100).toFixed(1)}%
              </td>
              <td className="px-1 py-1 border-b border-gray-100 text-center"><HMLBadge hml={row.hml} /></td>
              <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center bg-amber-50/40 ${row.month_count > 0 ? 'font-bold' : 'text-gray-300'}`}>
                {row.month_count || '-'}
              </td>
              <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center bg-amber-50/40 text-[10px] ${row.month_pct > row.overall_pct ? 'text-green-700 font-bold' : ''}`}>
                {(row.month_pct * 100).toFixed(1)}%
              </td>
              <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center bg-red-50/30 text-[10px] font-bold ${row.month_diff > 0 ? 'text-green-700' : row.month_diff < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                {(row.month_diff * 100) > 0 ? '+' : ''}{(row.month_diff * 100).toFixed(1)}%
              </td>
              <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center bg-green-50/30 font-bold ${row.cy_count > 0 ? '' : 'text-gray-300'}`}>{row.cy_count || '-'}</td>
              <td className="px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] bg-green-50/10">{(row.cy_pct * 100).toFixed(1)}%</td>
              <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] font-bold ${row.pct_diff > 0 ? 'text-green-700' : row.pct_diff < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                {(row.pct_diff * 100) > 0 ? '+' : ''}{(row.pct_diff * 100).toFixed(1)}%
              </td>
              <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] font-bold ${row.avg_diff > 0 ? 'text-blue-700' : row.avg_diff < 0 ? 'text-orange-600' : 'text-gray-400'}`}>
                {row.avg_diff > 0 ? '+' : ''}{row.avg_diff}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const defaultWidths = {
  num: 38, times: 48, avg: 42, pct: 50, hml: 38,
  mon: 42, mpct: 48, diff: 50,
  yr: 42, yrp: 42,
  pdiff: 50, adiff: 50
};

const PatternBadge = ({ pattern }) => {
  const colors = { H: 'bg-green-100 text-green-800', M: 'bg-amber-100 text-amber-800', L: 'bg-red-100 text-red-800' };
  const chars = typeof pattern === 'string' ? pattern.split('') : [];
  return (
    <div className="flex gap-0.5" data-testid={`pattern-${pattern}`}>
      {chars.map((c, i) => (
        <span key={i} className={`inline-flex items-center justify-center w-6 h-6 rounded text-[10px] font-bold ${colors[c] || 'bg-gray-100 text-gray-300 border border-dashed border-gray-300'}`}>
          {colors[c] ? c : '—'}
        </span>
      ))}
    </div>
  );
};

function MostAwaitedPanel({ awaited, loading }) {
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-amber-50 via-rose-50 to-indigo-50 border-2 border-amber-300 rounded-lg p-5 flex items-center gap-3" data-testid="most-awaited-loading">
        <CircleNotch size={20} className="animate-spin text-amber-600" />
        <span className="text-sm text-amber-900 font-medium">Computing Max-Confluence Picks…</span>
      </div>
    );
  }
  if (!awaited || awaited.error) return null;

  const full = awaited.full_match || [];
  const partial = awaited.two_of_three || [];
  const hasFull = full.length > 0;

  const Badge = ({ on, label, color }) => (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wide ${
      on ? (color === 'rose' ? 'bg-rose-200 text-rose-900 ring-1 ring-rose-400'
          : color === 'sky' ? 'bg-sky-200 text-sky-900 ring-1 ring-sky-400'
          : 'bg-amber-200 text-amber-900 ring-1 ring-amber-400')
      : 'bg-gray-200 text-gray-400 opacity-50 line-through'
    }`}>{label}</span>
  );

  const NumberCard = ({ row, kind }) => {
    const bayes = row.bayesian;
    const rev = row.reversion || {};
    const cycle = row.cycle || {};
    const isFull = kind === 'full';
    return (
      <div
        className={`relative p-3 rounded-lg ring-2 ${isFull
          ? 'bg-gradient-to-br from-amber-100 to-rose-100 ring-amber-500'
          : 'bg-white ring-gray-300'}`}
        data-testid={`awaited-${kind}-${row.number}`}
      >
        <div className="flex items-center justify-between mb-1.5">
          <div className={`text-4xl font-mono font-black ${isFull ? 'text-rose-700' : 'text-slate-800'}`}>{row.number}</div>
          <div className="flex flex-col items-end">
            <div className="text-[9px] uppercase tracking-wider text-gray-500 font-bold">Score</div>
            <div className={`text-xl font-mono font-black ${isFull ? 'text-amber-700' : 'text-slate-700'}`}>{row.composite_score}</div>
          </div>
        </div>
        <div className="flex gap-1 mb-2 flex-wrap">
          <Badge on={row.flags.overdue} label="OVERDUE" color="rose" />
          <Badge on={row.flags.cold} label="COLD" color="sky" />
          <Badge on={row.flags.bayes_high} label="BAYES↑" color="amber" />
        </div>
        <div className="text-[10px] text-gray-700 space-y-0.5 font-mono">
          {cycle.cycle_phase != null && <div>Cycle: <b>{cycle.cycle_phase}%</b> (gap {cycle.current_gap}/{cycle.avg_interval})</div>}
          {rev.state && <div>Rev: <b>{rev.state}</b> · lift {rev.lift} · overdue {rev.overdue_score}</div>}
          {bayes && <div>Bayes: <b>BF {bayes.bayes_factor}</b> @ {bayes.ball.replace('ball_','B')} (n={bayes.sample_count})</div>}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`rounded-lg p-5 border-2 ${hasFull
        ? 'bg-gradient-to-br from-amber-50 via-rose-50 to-indigo-50 border-amber-400 shadow-lg'
        : 'bg-slate-50 border-slate-300'}`}
      data-testid="most-awaited-panel"
    >
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <Target size={22} weight="fill" className={hasFull ? 'text-rose-600' : 'text-slate-500'} />
            <h3 className={`font-black text-lg tracking-tight ${hasFull ? 'text-rose-900' : 'text-slate-700'}`}>
              Most Awaited Numbers — Max-Confluence Picks
            </h3>
          </div>
          <p className="text-xs text-slate-600 mt-0.5">
            Intersects <b>W7 Cycle Phase OVERDUE</b> (&gt;{awaited.thresholds.overdue_cycle_phase_pct}%) ∩ <b>W46 Reversion COLD</b> (lift ≤ {awaited.thresholds.cold_lift_max}) ∩ <b>W22 Bayesian HIGH</b> (BF ≥ {awaited.thresholds.bayes_factor_min}, n ≥ {awaited.thresholds.bayes_sample_count_min})
          </p>
        </div>
        <div className="text-right">
          <div className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${hasFull ? 'bg-rose-600 text-white' : 'bg-slate-400 text-white'}`} data-testid="awaited-verdict">
            {awaited.verdict}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">Counts: full {awaited.counts.full_match} · 2-of-3 {awaited.counts.two_of_three} · 1-match {awaited.counts.one_match}</div>
        </div>
      </div>

      {hasFull ? (
        <>
          <div className="mb-2 text-xs font-bold uppercase tracking-wider text-rose-800">🎯 Full Confluence ({full.length})</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
            {full.map(r => <NumberCard key={r.number} row={r} kind="full" />)}
          </div>
        </>
      ) : (
        <div className="mb-4 p-3 bg-slate-100 border border-slate-300 rounded text-sm text-slate-700 italic">
          No number currently satisfies all three signals. Showing <b>2-of-3 matches</b> as near-miss picks below.
        </div>
      )}

      {partial.length > 0 && (
        <>
          <div className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-700">
            ⚠ 2-of-3 Matches ({partial.length}) <span className="text-slate-500 font-normal normal-case">— near-miss picks</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {partial.slice(0, 12).map(r => <NumberCard key={r.number} row={r} kind="partial" />)}
          </div>
        </>
      )}
    </div>
  );
}

function CyclePhaseTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBallTab, setActiveBallTab] = useState('cross-ball');
  const [sortKey, setSortKey] = useState('cycle_phase');
  const [sortDir, setSortDir] = useState('desc');
  const [awaited, setAwaited] = useState(null);
  const [awaitedLoading, setAwaitedLoading] = useState(true);
  const [rebound, setRebound] = useState(null);
  const [crossBall, setCrossBall] = useState(null);
  const [postShift, setPostShift] = useState(null);
  const [perBall, setPerBall] = useState(null);
  const [pbActiveBall, setPbActiveBall] = useState('ball_1');

  useEffect(() => {
    axios.get(`${API}/analysis/jackpot-cycle`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => { toast.error('Failed to load cycle data'); setLoading(false); });
    axios.get(`${API}/analysis/most-awaited?tolerance=medium`)
      .then(r => { setAwaited(r.data); setAwaitedLoading(false); })
      .catch(() => { setAwaitedLoading(false); });
    axios.get(`${API}/analysis/hml-rebound`)
      .then(r => setRebound(r.data))
      .catch(() => {});
    axios.get(`${API}/analysis/hml-rebound/cross-ball`)
      .then(r => setCrossBall(r.data))
      .catch(() => {});
    axios.get(`${API}/analysis/hml-rebound/post-shift-return`)
      .then(r => setPostShift(r.data))
      .catch(() => {});
    axios.get(`${API}/analysis/hml-rebound/per-ball-summary`)
      .then(r => setPerBall(r.data))
      .catch(() => {});
  }, []);

  const handleSort = (k) => {
    if (k === sortKey) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  const ballLabels = { 'cross-ball': 'Cross-Ball', ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

  const StatusBadge = ({ status }) => {
    const cls = status === 'OVERDUE' ? 'bg-rose-100 text-rose-800 ring-1 ring-rose-300'
              : status === 'DUE' ? 'bg-amber-100 text-amber-800 ring-1 ring-amber-300'
              : 'bg-emerald-50 text-emerald-700';
    return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${cls}`}>{status}</span>;
  };

  const CycleTable = ({ numbers }) => {
    const sorted = [...numbers].sort((a, b) => {
      const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    const topPredicted = new Set([...numbers].sort((a, b) => (b.cycle_phase || 0) - (a.cycle_phase || 0)).slice(0, 5).map(n => n.number));
    return (
      <div className="overflow-x-auto" data-testid="cycle-phase-table">
        <table className="w-full text-xs">
          <thead className="bg-gray-100">
            <tr>
              {[['number','#'],['appearances','Apps'],['avg_interval','Avg Int'],['min_interval','Min'],['max_interval','Max'],['current_gap','Gap'],['cycle_phase','Phase%']].map(([k,l]) => (
                <th key={k} className="px-2 py-2 text-[10px] uppercase cursor-pointer hover:bg-gray-200 text-center" onClick={() => handleSort(k)}>{l}{sortKey===k ? (sortDir==='desc' ? ' ▾' : ' ▴') : ''}</th>
              ))}
              <th className="px-2 py-2 text-center text-[10px] uppercase">Status</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((n, idx) => (
              <tr key={n.number} className={topPredicted.has(n.number) ? 'bg-sky-100 border-l-4 border-sky-600' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`cycle-row-${n.number}`}>
                <td className="px-2 py-1 border-b font-mono font-bold text-center">{n.number}</td>
                <td className="px-2 py-1 border-b font-mono text-center">{n.appearances}</td>
                <td className="px-2 py-1 border-b font-mono text-center font-bold">{n.avg_interval}</td>
                <td className="px-2 py-1 border-b font-mono text-center text-[10px] text-gray-500">{n.min_interval}</td>
                <td className="px-2 py-1 border-b font-mono text-center text-[10px] text-gray-500">{n.max_interval}</td>
                <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.current_gap > n.avg_interval ? 'text-rose-700' : 'text-emerald-700'}`}>{n.current_gap}</td>
                <td className={`px-2 py-1 border-b font-mono text-center font-bold ${n.cycle_phase > 120 ? 'text-rose-700' : n.cycle_phase > 80 ? 'text-amber-700' : ''}`}>{n.cycle_phase}%</td>
                <td className="px-2 py-1 border-b text-center"><StatusBadge status={n.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) return <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;
  if (!data) return null;

  const balls = data.balls || {};
  const currentData = activeBallTab === 'cross-ball' ? (data.number_cycles || []) : (balls[activeBallTab] || []);
  const overdue = currentData.filter(n => n.status === 'OVERDUE');

  return (
    <div className="space-y-6" data-testid="cycle-phase-tab-redirect">
      <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-300 rounded-lg p-6 text-center">
        <div className="inline-block px-3 py-1 bg-indigo-700 text-white rounded-full text-[11px] font-black tracking-wider mb-3">RETIRED</div>
        <h2 className="text-xl font-bold text-indigo-900 mb-2">Cycle Phase data has moved to the Master Sheet</h2>
        <p className="text-sm text-indigo-700 max-w-2xl mx-auto leading-relaxed">
          All Cycle Phase analytics — including <b>Most Awaited Numbers (Max-Confluence picks)</b>, per-ball cycle %, avg interval, min/max gap, status (NORMAL/DUE/OVERDUE), HML Rebound, Cross-Ball Reappearance, and Post-Shift Return — are now consolidated in the new <b>Master Sheet</b> tab. The Master Sheet auto-updates with the latest draw and shows everything in one per-ball matrix.
        </p>
        <p className="text-[12px] text-indigo-600 mt-4">
          → Click the <b>Master Sheet</b> tab above to access all signals.
        </p>
      </div>
    </div>
  );
}

/* ============ HML Rebound Block — per Ball × Tier max gap stats ============ */
function HmlReboundBlock({ rebound }) {
  if (!rebound || !rebound.balls) {
    return (
      <div className="bg-white border rounded-lg p-4 text-xs text-gray-500" data-testid="hml-rebound-block">
        Loading HML rebound stats...
      </div>
    );
  }

  const tierColors = {
    H: { bg: 'bg-rose-50', border: 'border-rose-300', head: 'bg-rose-600', accent: 'text-rose-700', chip: 'bg-rose-100 text-rose-800' },
    M: { bg: 'bg-amber-50', border: 'border-amber-300', head: 'bg-amber-600', accent: 'text-amber-700', chip: 'bg-amber-100 text-amber-800' },
    L: { bg: 'bg-emerald-50', border: 'border-emerald-300', head: 'bg-emerald-600', accent: 'text-emerald-700', chip: 'bg-emerald-100 text-emerald-800' },
  };

  return (
    <div className="bg-white border-2 border-slate-300 rounded-lg overflow-hidden" data-testid="hml-rebound-block">
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-4 py-3">
        <h3 className="font-bold flex items-center gap-2">
          <span className="inline-block px-2 py-0.5 bg-amber-400 text-slate-900 rounded text-[10px] font-black">REBOUND</span>
          HML Rebound Analysis — Per Ball × Tier Maximum Gap
        </h3>
        <p className="text-[11px] text-white/80 mt-0.5">
          For each ball position × tier (H/M/L), the longest historical gap before reappearance, plus currently-absent-in-2026 numbers and how their current gap compares.{' '}
          <b className="text-amber-300">Tier Max</b> answers: "If an H/M/L number from this ball is missing, how long can it stay missing historically?"
        </p>
      </div>

      <div className="p-4 grid grid-cols-1 lg:grid-cols-5 gap-3" data-testid="hml-rebound-grid">
        {rebound.balls.map(b => (
          <div key={b.ball} className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden" data-testid={`hml-rebound-${b.ball}`}>
            <div className="bg-slate-900 text-white px-3 py-2 text-center">
              <div className="font-bold text-sm">{b.label}</div>
            </div>
            <div className="p-2 space-y-2">
              {['H', 'M', 'L'].map(tier => {
                const t = b.tiers[tier];
                if (!t) return null;
                const c = tierColors[tier];
                const mo = t.most_overdue_absent;
                return (
                  <div key={tier} className={`${c.bg} ${c.border} border rounded p-2`} data-testid={`hml-tier-${b.ball}-${tier}`}>
                    {/* Tier header */}
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <span className={`inline-flex w-6 h-6 rounded items-center justify-center font-black text-white text-xs ${c.head}`}>{tier}</span>
                        <span className={`text-[10px] font-bold ${c.accent}`}>{t.count} nums</span>
                      </div>
                      <div className="text-right">
                        <div className={`text-[9px] uppercase font-bold tracking-wider ${c.accent}`}>Max Rebound</div>
                        <div className={`text-lg font-mono font-black ${c.accent}`}>{t.tier_max_gap}</div>
                        <div className="text-[8px] text-gray-500">draws</div>
                      </div>
                    </div>

                    {/* Avg rebound rate sub-stats */}
                    <div className="grid grid-cols-2 gap-1 mb-1.5">
                      <div className="bg-white rounded px-1.5 py-1 border border-gray-200">
                        <div className="text-[8px] uppercase font-bold tracking-wider text-gray-500">Avg Typical Gap</div>
                        <div className={`text-sm font-mono font-black ${c.accent}`}>{t.tier_avg_typical_gap}</div>
                        <div className="text-[8px] text-gray-400">draws between hits</div>
                      </div>
                      <div className="bg-white rounded px-1.5 py-1 border border-gray-200">
                        <div className="text-[8px] uppercase font-bold tracking-wider text-gray-500">Avg Max Gap</div>
                        <div className={`text-sm font-mono font-black ${c.accent}`}>{t.tier_avg_max_gap}</div>
                        <div className="text-[8px] text-gray-400">avg of records</div>
                      </div>
                    </div>

                    {/* Top max holder */}
                    {t.top_max_holder && t.top_max_holder.max_gap > 0 && (
                      <div className="text-[9px] text-gray-600 mb-1">
                        Longest streak holder: <b>#{t.top_max_holder.number}</b> ({t.top_max_holder.max_gap} draws)
                      </div>
                    )}

                    {/* Absent in 2026 */}
                    <div className="bg-white rounded p-1.5 border border-gray-200 mt-1.5">
                      <div className="text-[9px] uppercase font-bold tracking-wider text-gray-500 mb-1">
                        Absent in 2026: <span className={c.accent}>{t.absent_in_2026.length}</span>
                      </div>
                      {t.absent_in_2026.length === 0 ? (
                        <div className="text-[10px] text-emerald-700 italic">All present ✓</div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {t.absent_in_2026.slice(0, 12).map(n => {
                            const exceeded = n.exceeded_max;
                            return (
                              <div key={n.number} className={`inline-flex flex-col items-center ${exceeded ? 'ring-2 ring-red-500 rounded' : ''}`} data-testid={`absent-${b.ball}-${tier}-${n.number}`}>
                                <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-black text-[11px] ${exceeded ? 'bg-red-600 text-white' : c.chip}`}>
                                  {n.number}
                                </span>
                                <span className="text-[8px] font-mono text-gray-500">{n.current_gap}/{n.max_gap || '–'}</span>
                              </div>
                            );
                          })}
                          {t.absent_in_2026.length > 12 && (
                            <span className="text-[9px] text-gray-400 self-center">+{t.absent_in_2026.length - 12}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Most overdue */}
                    {mo && mo.max_gap > 0 && (
                      <div className={`mt-1.5 rounded p-1.5 text-[10px] ${mo.exceeded_max ? 'bg-red-100 border border-red-400' : 'bg-white border border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-600">Most overdue:</span>
                          <span className={`font-mono font-black ${mo.exceeded_max ? 'text-red-700' : c.accent}`}>
                            #{mo.number}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <span className="text-gray-500 text-[9px]">Gap {mo.current_gap} / Max {mo.max_gap}</span>
                          {mo.exceeded_max && <span className="text-[8px] font-black text-red-700 bg-red-200 px-1 rounded">⚠ BEYOND MAX</span>}
                        </div>
                        <div className="text-[8px] text-gray-400 mt-0.5">Last seen: {mo.last_seen_year || '—'}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-blue-50 border-t border-blue-200 px-4 py-2 text-[10px] text-blue-800">
        <b>Reading:</b> Each cell shows <b>Number ({'gap_now / hist_max'})</b>. When current gap &gt; historical max (red ring), the number is in unprecedented absence territory — strongest reappearance signal.{' '}
        <b className="text-rose-700">H</b> = top 50% coverage numbers,{' '}
        <b className="text-amber-700">M</b> = next 30%,{' '}
        <b className="text-emerald-700">L</b> = remaining 20%.
      </div>
    </div>
  );
}

/* ============ Per-Ball Master View — longest dry × HML × cross-ball ETA ============ */
function PerBallMasterBlock({ perBall, activeBall, setActiveBall }) {
  if (!perBall || !perBall.balls) {
    return (
      <div className="bg-white border rounded-lg p-4 text-xs text-gray-500" data-testid="per-ball-master-block">
        Loading per-ball master view...
      </div>
    );
  }

  const current = perBall.balls.find(b => b.ball === activeBall) || perBall.balls[0];
  const tierMeta = {
    H: { label: 'HIGH', color: 'rose', accent: 'text-rose-700', bg: 'bg-rose-50', border: 'border-rose-300', head: 'from-rose-600 to-red-600' },
    M: { label: 'MID', color: 'amber', accent: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-300', head: 'from-amber-600 to-orange-600' },
    L: { label: 'LOW', color: 'emerald', accent: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-300', head: 'from-emerald-600 to-teal-600' },
  };

  return (
    <div className="bg-white border-2 border-indigo-300 rounded-lg overflow-hidden shadow-md" data-testid="per-ball-master-block">
      <div className="bg-gradient-to-r from-indigo-700 via-purple-700 to-fuchsia-700 text-white px-4 py-3">
        <h3 className="font-bold flex items-center gap-2">
          <span className="inline-block px-2 py-0.5 bg-yellow-300 text-slate-900 rounded text-[10px] font-black">MASTER</span>
          Per-Ball Master View — Longest Dry × HML Tier × Cross-Ball ETA
        </h3>
        <p className="text-[11px] text-white/90 mt-0.5">
          Pick a ball. For each tier (H/M/L), see the numbers most overdue at that ball position, their typical rebound rate, whether they recently surfaced at <i>another</i> ball, and the predicted ETA to return to this ball.
        </p>
      </div>

      {/* Ball selector */}
      <div className="px-4 py-3 bg-slate-100 border-b border-slate-200 flex gap-2 flex-wrap" data-testid="pb-ball-selector">
        {perBall.balls.map(b => (
          <button
            key={b.ball}
            onClick={() => setActiveBall(b.ball)}
            data-testid={`pb-ball-${b.ball}`}
            className={`px-4 py-1.5 rounded-lg font-bold text-sm transition-colors ${
              activeBall === b.ball
                ? 'bg-indigo-700 text-white shadow-md'
                : 'bg-white text-slate-700 border border-slate-300 hover:bg-indigo-50'
            }`}
          >
            {b.label}
          </button>
        ))}
      </div>

      {/* Three tier tables stacked */}
      <div className="p-4 space-y-4">
        {['H', 'M', 'L'].map(tier => {
          const meta = tierMeta[tier];
          const rows = (current.tiers[tier] || []).slice(0, 12);
          if (rows.length === 0) return null;
          return (
            <div key={tier} className={`${meta.border} border-2 rounded-lg overflow-hidden`} data-testid={`pb-tier-${tier}`}>
              <div className={`bg-gradient-to-r ${meta.head} text-white px-3 py-2 flex items-center justify-between`}>
                <div className="font-bold text-sm flex items-center gap-2">
                  <span className="inline-flex w-7 h-7 rounded items-center justify-center bg-white/20 font-black">{tier}</span>
                  {meta.label} Tier — {current.label}
                </div>
                <span className="text-[10px] text-white/80">{current.tiers[tier].length} numbers in tier</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid={`pb-table-${current.ball}-${tier}`}>
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-2 py-2 text-center font-bold text-slate-700">#</th>
                      <th className="px-2 py-2 text-center font-bold text-slate-700">Curr Gap</th>
                      <th className="px-2 py-2 text-center font-bold text-slate-700">Avg/Max</th>
                      <th className="px-2 py-2 text-center font-bold text-slate-700">Last Seen<br/><span className="font-normal text-[9px]">(at this ball)</span></th>
                      <th className="px-2 py-2 text-left font-bold text-violet-700">Recent Surface @ Other Ball</th>
                      <th className="px-2 py-2 text-center font-bold text-cyan-700">Avg Return<br/><span className="font-normal text-[9px]">to this ball</span></th>
                      <th className="px-2 py-2 text-center font-bold text-emerald-700">ETA</th>
                      <th className="px-2 py-2 text-center font-bold text-amber-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => {
                      const cb = r.cross_ball_event;
                      const isOverdue = r.eta_overdue;
                      const exceededMax = r.exceeded_max;
                      const rowBg = exceededMax ? 'bg-red-50' : isOverdue ? 'bg-amber-50' : 'bg-white';
                      return (
                        <tr key={r.number} className={`border-b border-slate-100 ${rowBg}`} data-testid={`pb-row-${current.ball}-${tier}-${r.number}`}>
                          <td className="px-2 py-2 text-center">
                            <span className={`inline-flex w-9 h-9 rounded-full items-center justify-center font-mono font-black text-white ${meta.head.replace('from-', 'bg-').split(' ')[0]}`}>
                              {r.number}
                            </span>
                          </td>
                          <td className="px-2 py-2 text-center font-mono">
                            <span className={`font-bold ${exceededMax ? 'text-red-700' : isOverdue ? 'text-amber-700' : 'text-slate-700'}`}>
                              {r.current_gap}
                            </span>
                            {r.absent_in_2026_at_ball && (
                              <span className="block text-[8px] text-red-600 font-bold">absent 2026</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-center font-mono text-[11px] text-slate-600">
                            <div>{r.avg_gap}</div>
                            <div className="text-[9px] text-slate-400">max {r.max_gap}</div>
                          </td>
                          <td className="px-2 py-2 text-center text-slate-600 text-[11px]">
                            {r.last_year_at_ball || '—'}
                          </td>
                          <td className="px-2 py-2">
                            {cb ? (
                              <div className="flex items-center gap-1.5">
                                <span className="inline-block px-1.5 py-0.5 bg-violet-100 text-violet-800 rounded font-bold text-[10px]">{cb.ball_label}</span>
                                <span className="text-[10px] text-slate-500 font-mono">{cb.draws_ago_from_now} draws ago</span>
                                {cb.tier_at_other_ball && (
                                  <span className="text-[9px] text-slate-400">({cb.tier_at_other_ball})</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-[10px] text-slate-400 italic">no recent surface</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-center font-mono">
                            {r.expected_return_to_ball !== null ? (
                              <span className="font-bold text-cyan-700">{r.expected_return_to_ball}</span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                            {r.return_events > 0 && (
                              <div className="text-[8px] text-slate-400">n={r.return_events}, {r.return_min}-{r.return_max}</div>
                            )}
                          </td>
                          <td className="px-2 py-2 text-center font-mono">
                            {r.eta !== null ? (
                              <span className={`font-bold ${isOverdue ? 'text-red-700' : 'text-emerald-700'}`}>
                                {r.eta === 0 ? 'now' : `~${r.eta}`}
                              </span>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-center text-[10px]">
                            {exceededMax ? (
                              <span className="inline-block px-1.5 py-0.5 rounded bg-red-600 text-white font-black">⚠ BEYOND MAX</span>
                            ) : isOverdue ? (
                              <span className="inline-block px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 font-bold">OVERDUE</span>
                            ) : cb ? (
                              <span className="inline-block px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700 font-bold">SHIFTED</span>
                            ) : (
                              <span className="inline-block px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">NORMAL</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-indigo-50 border-t border-indigo-200 px-4 py-2 text-[10px] text-indigo-800 leading-relaxed">
        <b>Reading:</b> <b>Curr Gap</b> = draws since last appearance at this ball. <b>Avg/Max</b> = typical rebound rate / record drought at this ball. <b>Recent Surface</b> = if the number appeared at any OTHER ball during this absence (with which tier classification at that ball). <b>Avg Return</b> = historical avg draws to come back to this ball after surfacing elsewhere. <b>ETA</b> = expected remaining draws until the number returns to this ball.{' '}
        <span className="text-red-700 font-bold">⚠ BEYOND MAX</span> = current gap exceeded historical record (strongest reappearance signal).{' '}
        <span className="text-amber-700 font-bold">OVERDUE</span> = past avg expected return.{' '}
        <span className="text-cyan-700 font-bold">SHIFTED</span> = surfaced at another ball, ETA based on shift-history.
      </div>
    </div>
  );
}



/* ============ Cross-Ball Reappearance Verdict ============ */
function CrossBallReboundBlock({ crossBall }) {
  if (!crossBall || !crossBall.summary) {
    return (
      <div className="bg-white border rounded-lg p-4 text-xs text-gray-500" data-testid="cross-ball-rebound-block">
        Loading cross-ball reappearance stats...
      </div>
    );
  }

  const s = crossBall.summary;
  const ballLabels = crossBall.ball_labels || {};
  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
  const verdictWord = s.cross_pct > 55 ? 'CROSS-BALL' : s.same_pct > 55 ? 'SAME-BALL' : 'BALANCED';
  const verdictColor = s.cross_pct > 55 ? 'text-violet-600' : s.same_pct > 55 ? 'text-emerald-600' : 'text-amber-600';
  const verdictBg = s.cross_pct > 55 ? 'from-violet-600 to-fuchsia-600' : s.same_pct > 55 ? 'from-emerald-600 to-teal-600' : 'from-amber-500 to-orange-500';

  return (
    <div className="bg-white border-2 border-violet-300 rounded-lg overflow-hidden shadow-md" data-testid="cross-ball-rebound-block">
      <div className={`bg-gradient-to-r ${verdictBg} text-white px-4 py-3`}>
        <h3 className="font-bold flex items-center gap-2">
          <span className="inline-block px-2 py-0.5 bg-yellow-300 text-slate-900 rounded text-[10px] font-black">VERDICT</span>
          When a Dry Number Reappears — Same Ball or Different Ball?
        </h3>
        <p className="text-[11px] text-white/85 mt-0.5">
          For every "rebound after dryness" event in history (gap ≥ 1.5× number's avg interval), did the number return at the SAME ball position it was last seen — or did it shift to a DIFFERENT ball?
        </p>
      </div>

      {/* The Big Verdict */}
      <div className="bg-gradient-to-br from-slate-50 to-slate-100 px-5 py-4 border-b border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <div className="md:col-span-1 text-center">
            <div className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Verdict</div>
            <div className={`text-3xl font-black mt-1 ${verdictColor}`}>{verdictWord}</div>
            <div className="text-[10px] text-gray-500 mt-1">Based on {s.strong_dryness_rebounds} rebound events</div>
          </div>
          <div className="md:col-span-2 grid grid-cols-2 gap-3">
            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-3 text-center" data-testid="verdict-same">
              <div className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider">Same Ball</div>
              <div className="text-3xl font-mono font-black text-emerald-700 mt-1">{s.same_pct}%</div>
              <div className="text-[10px] text-emerald-600 mt-0.5">{s.same_position_rebounds} events</div>
            </div>
            <div className="bg-violet-50 border-2 border-violet-300 rounded-lg p-3 text-center" data-testid="verdict-cross">
              <div className="text-[10px] text-violet-700 font-bold uppercase tracking-wider">Different Ball</div>
              <div className="text-3xl font-mono font-black text-violet-700 mt-1">{s.cross_pct}%</div>
              <div className="text-[10px] text-violet-600 mt-0.5">{s.cross_position_rebounds} events</div>
            </div>
          </div>
        </div>
        <p className="text-[11px] text-slate-700 mt-3 leading-relaxed bg-white rounded p-2 border border-slate-200">
          <b>Reading:</b> {s.cross_pct > 55 ? (
            <>Yes — your hypothesis holds. <b className="text-violet-700">{s.cross_pct}% of dry-number rebounds historically arrive at a DIFFERENT ball position</b>, not the original one. So when #2 returns to Ball 1 specifically, that's the minority outcome — it's more likely to surface at another ball first.</>
          ) : s.same_pct > 55 ? (
            <>No — the bias is opposite: <b className="text-emerald-700">{s.same_pct}% of dry-number rebounds return to the SAME ball position</b>. Numbers tend to "stick" to their typical position.</>
          ) : (
            <>Roughly balanced — about as likely to return to the same ball ({s.same_pct}%) as a different ball ({s.cross_pct}%).</>
          )}
        </p>
      </div>

      {/* Per-source ball breakdown */}
      <div className="p-4">
        <div className="text-[10px] uppercase font-bold tracking-wider text-gray-600 mb-2">When last seen at Ball X — where does it reappear?</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="per-from-ball-table">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-3 py-2 text-left font-bold text-slate-700">Last Seen At</th>
                <th className="px-2 py-2 text-center font-bold text-slate-700">Events</th>
                <th className="px-2 py-2 text-center font-bold text-emerald-700">→ Same</th>
                <th className="px-2 py-2 text-center font-bold text-violet-700">→ Cross</th>
                {ballKeys.map(bk => (
                  <th key={bk} className="px-2 py-2 text-center font-bold text-slate-600">→ {ballLabels[bk]}</th>
                ))}
                <th className="px-2 py-2 text-center font-bold text-amber-700">Bias</th>
              </tr>
            </thead>
            <tbody>
              {crossBall.per_from_ball.map(fb => {
                const total = Object.values(fb.transitions).reduce((sum, v) => sum + v, 0);
                const isCrossBias = fb.cross_pct > 55;
                const isSameBias = fb.same_pct > 55;
                return (
                  <tr key={fb.from} className="border-b border-slate-100" data-testid={`from-ball-${fb.from}`}>
                    <td className="px-3 py-2 font-bold text-slate-800">{fb.label}</td>
                    <td className="px-2 py-2 text-center font-mono text-gray-600">{fb.rebounds}</td>
                    <td className={`px-2 py-2 text-center font-mono font-bold ${isSameBias ? 'bg-emerald-100 text-emerald-700' : 'text-emerald-600'}`}>
                      {fb.same_pct}%
                    </td>
                    <td className={`px-2 py-2 text-center font-mono font-bold ${isCrossBias ? 'bg-violet-100 text-violet-700' : 'text-violet-600'}`}>
                      {fb.cross_pct}%
                    </td>
                    {ballKeys.map(bk => {
                      const c = fb.transitions[bk] || 0;
                      const pct = total > 0 ? (c / total * 100) : 0;
                      const isSelf = bk === fb.from;
                      const intensity = pct >= 30 ? 'bg-emerald-200 text-emerald-900 font-bold' :
                                       pct >= 20 ? 'bg-emerald-100 text-emerald-800' :
                                       pct >= 10 ? 'bg-amber-50 text-amber-700' :
                                       'text-gray-500';
                      return (
                        <td key={bk} className={`px-2 py-2 text-center font-mono ${intensity} ${isSelf ? 'ring-1 ring-inset ring-emerald-400' : ''}`} title={`${c} events`}>
                          {pct.toFixed(0)}%
                        </td>
                      );
                    })}
                    <td className="px-2 py-2 text-center font-bold text-[10px]">
                      <span className={`inline-block px-1.5 py-0.5 rounded ${isCrossBias ? 'bg-violet-200 text-violet-800' : isSameBias ? 'bg-emerald-200 text-emerald-800' : 'bg-gray-200 text-gray-700'}`}>
                        {isCrossBias ? 'CROSS' : isSameBias ? 'STICKY' : 'BALANCED'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-[10px] text-gray-500 mt-2 italic leading-relaxed">
          <b>Key insight:</b> Ball 1 and Ball 5 are <b>STICKY</b> positions — the lowest/highest numbers naturally land there due to ascending-sort. Middle balls (2/3/4) shift positions much more often. So a dry low number (e.g., #2 last at Ball 1) is statistically more likely to come back at Ball 1.
        </div>
      </div>

      {/* Currently Dry → Likely Return Position */}
      {crossBall.dry_predictions && crossBall.dry_predictions.length > 0 && (
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <div className="text-[10px] uppercase font-bold tracking-wider text-gray-600 mb-2">
            Currently Dry Numbers — Predicted Return Ball
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="dry-predictions-table">
              <thead className="bg-slate-200">
                <tr>
                  <th className="px-2 py-1.5 text-center font-bold">#</th>
                  <th className="px-2 py-1.5 text-left font-bold">Last Seen</th>
                  <th className="px-2 py-1.5 text-center font-bold">Gap / Avg</th>
                  <th className="px-2 py-1.5 text-left font-bold">Top Predicted Ball</th>
                  <th className="px-2 py-1.5 text-left font-bold">Distribution (%)</th>
                </tr>
              </thead>
              <tbody>
                {crossBall.dry_predictions.slice(0, 10).map(p => {
                  const sameAsLast = p.top_predicted_ball.ball === p.last_pos;
                  return (
                    <tr key={p.number} className={`border-b border-slate-100 ${p.is_overdue ? 'bg-red-50' : 'bg-white'}`} data-testid={`dry-pred-${p.number}`}>
                      <td className="px-2 py-1.5 text-center font-mono font-black text-base">
                        <span className="inline-flex w-8 h-8 rounded-full items-center justify-center bg-slate-700 text-white">{p.number}</span>
                      </td>
                      <td className="px-2 py-1.5 text-slate-700">{p.last_pos_label}</td>
                      <td className="px-2 py-1.5 text-center font-mono">
                        <span className={p.is_overdue ? 'text-red-600 font-bold' : 'text-gray-600'}>{p.current_gap}</span>
                        <span className="text-gray-400"> / {p.avg_gap}</span>
                      </td>
                      <td className="px-2 py-1.5 font-bold">
                        <span className={sameAsLast ? 'text-emerald-700' : 'text-violet-700'}>
                          {p.top_predicted_ball.label} ({p.top_predicted_ball.pct}%)
                        </span>
                        {sameAsLast ? (
                          <span className="ml-1 text-[9px] bg-emerald-100 text-emerald-700 px-1 py-0.5 rounded">STICKY</span>
                        ) : (
                          <span className="ml-1 text-[9px] bg-violet-100 text-violet-700 px-1 py-0.5 rounded">SHIFT</span>
                        )}
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex gap-2 items-end text-[9px]">
                          {p.distribution.slice(0, 5).map(d => (
                            <span key={d.ball} className={`inline-flex flex-col items-center ${d.ball === p.last_pos ? 'font-black' : ''}`}>
                              <span className="text-slate-500">{d.label.replace('Ball ', 'B')}</span>
                              <span className="font-mono font-bold text-slate-700">{d.pct}%</span>
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="text-[10px] text-gray-500 mt-2 italic">
            <b className="text-emerald-700">STICKY</b> = predicted to return at the same ball as last seen. <b className="text-violet-700">SHIFT</b> = predicted to land at a different ball than last seen. Red row = overdue (gap &gt; 1.5× avg).
          </div>
        </div>
      )}
    </div>
  );
}



/* ============ Post-Shift Return to H-Position ============ */
function PostShiftReturnBlock({ postShift }) {
  if (!postShift || !postShift.summary) {
    return (
      <div className="bg-white border rounded-lg p-4 text-xs text-gray-500" data-testid="post-shift-block">
        Loading post-shift return stats...
      </div>
    );
  }

  const s = postShift.summary;
  const shifted = postShift.currently_shifted || [];
  const dist = postShift.distribution || [];
  const maxBucketPct = Math.max(...dist.map(b => b.pct), 1);

  return (
    <div className="bg-white border-2 border-cyan-300 rounded-lg overflow-hidden shadow-md" data-testid="post-shift-block">
      <div className="bg-gradient-to-r from-cyan-600 to-teal-700 text-white px-4 py-3">
        <h3 className="font-bold flex items-center gap-2">
          <span className="inline-block px-2 py-0.5 bg-yellow-300 text-slate-900 rounded text-[10px] font-black">SHIFT-RETURN</span>
          After a Number Surfaces at a Non-H Ball — How Long Until It Returns to Its H-Ball?
        </h3>
        <p className="text-[11px] text-white/90 mt-0.5">
          When a number's true H-tier ball is dry but the number suddenly appears at a different ball (e.g. #2 at Ball 2 instead of its H-position Ball 1), how many draws later does it typically return to its actual H position?
        </p>
      </div>

      {/* Aggregate verdict */}
      <div className="bg-gradient-to-br from-slate-50 to-cyan-50 px-5 py-4 border-b border-slate-200 grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white rounded-lg p-3 border-2 border-cyan-300 text-center" data-testid="ps-avg">
          <div className="text-[10px] text-cyan-700 font-bold uppercase tracking-wider">Avg Return</div>
          <div className="text-3xl font-mono font-black text-cyan-700 mt-1">{s.avg_gap}</div>
          <div className="text-[10px] text-cyan-600 mt-0.5">draws</div>
        </div>
        <div className="bg-white rounded-lg p-3 border border-slate-200 text-center" data-testid="ps-median">
          <div className="text-[10px] text-slate-600 font-bold uppercase tracking-wider">Median</div>
          <div className="text-3xl font-mono font-black text-slate-700 mt-1">{s.median_gap}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">draws</div>
        </div>
        <div className="bg-white rounded-lg p-3 border border-emerald-200 text-center">
          <div className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider">Min</div>
          <div className="text-3xl font-mono font-black text-emerald-700 mt-1">{s.min_gap}</div>
          <div className="text-[10px] text-emerald-600 mt-0.5">fastest return</div>
        </div>
        <div className="bg-white rounded-lg p-3 border border-rose-200 text-center">
          <div className="text-[10px] text-rose-700 font-bold uppercase tracking-wider">Max</div>
          <div className="text-3xl font-mono font-black text-rose-700 mt-1">{s.max_gap}</div>
          <div className="text-[10px] text-rose-600 mt-0.5">longest delay</div>
        </div>
        <div className="bg-white rounded-lg p-3 border border-slate-200 text-center">
          <div className="text-[10px] text-slate-600 font-bold uppercase tracking-wider">Sample</div>
          <div className="text-3xl font-mono font-black text-slate-700 mt-1">{s.events}</div>
          <div className="text-[10px] text-slate-500 mt-0.5">historic events</div>
        </div>
      </div>

      {/* Distribution chart */}
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="text-[10px] uppercase font-bold tracking-wider text-slate-600 mb-2">Distribution — How quickly does the H-return happen?</div>
        <div className="space-y-1.5">
          {dist.map(b => (
            <div key={b.range} className="flex items-center gap-2 text-xs" data-testid={`ps-bucket-${b.range}`}>
              <span className="w-20 text-right font-mono text-slate-700 font-bold">{b.range} draws</span>
              <div className="flex-1 bg-slate-200 rounded-full h-4 overflow-hidden">
                <div className="bg-gradient-to-r from-cyan-500 to-teal-500 h-4 rounded-full" style={{ width: `${(b.pct / maxBucketPct) * 100}%` }} />
              </div>
              <span className="w-20 font-mono text-slate-700"><b>{b.pct}%</b> ({b.count})</span>
            </div>
          ))}
        </div>
        <div className="text-[10px] text-slate-500 mt-2 italic">
          Most H-returns happen within <b className="text-cyan-700">11–50 draws</b> ({(dist[4]?.pct || 0) + (dist[5]?.pct || 0)}% of all events). Short returns (1–6 draws) cover {(dist[0]?.pct || 0) + (dist[1]?.pct || 0) + (dist[2]?.pct || 0)}%.
        </div>
      </div>

      {/* Currently shifted numbers */}
      {shifted.length > 0 && (
        <div className="p-4">
          <div className="text-[10px] uppercase font-bold tracking-wider text-cyan-700 mb-2">
            Currently Shifted Numbers — Predicted Days to H-Return
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="ps-shifted-table">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-2 py-2 text-center font-bold text-slate-700">#</th>
                  <th className="px-2 py-2 text-left font-bold text-slate-700">H Position(s)</th>
                  <th className="px-2 py-2 text-left font-bold text-slate-700">Last Surfaced At</th>
                  <th className="px-2 py-2 text-center font-bold text-slate-700">Current Gap</th>
                  <th className="px-2 py-2 text-center font-bold text-cyan-700">Avg H-Return</th>
                  <th className="px-2 py-2 text-center font-bold text-slate-700">Range (min-max)</th>
                  <th className="px-2 py-2 text-center font-bold text-slate-700">Events</th>
                  <th className="px-2 py-2 text-center font-bold text-amber-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {shifted.slice(0, 15).map(p => {
                  const remaining = Math.max(0, Math.round((p.avg_post_shift_gap || 0) - p.current_shift_gap));
                  const overdue = p.is_overdue_post_shift;
                  return (
                    <tr key={p.number} className={`border-b border-slate-100 ${overdue ? 'bg-red-50' : 'bg-white'}`} data-testid={`ps-shifted-${p.number}`}>
                      <td className="px-2 py-2 text-center">
                        <span className="inline-flex w-9 h-9 rounded-full items-center justify-center bg-slate-700 text-white font-mono font-black">{p.number}</span>
                      </td>
                      <td className="px-2 py-2 font-bold text-slate-800">{p.h_position_labels.join(', ')}</td>
                      <td className="px-2 py-2 text-slate-700">
                        {p.last_pos_label}
                        <span className="text-[9px] text-slate-400 ml-1">({p.last_seen_year})</span>
                      </td>
                      <td className="px-2 py-2 text-center font-mono">
                        <span className={overdue ? 'text-red-600 font-bold' : 'text-slate-700 font-bold'}>{p.current_shift_gap}</span>
                      </td>
                      <td className="px-2 py-2 text-center font-mono font-black text-cyan-700">{p.avg_post_shift_gap}</td>
                      <td className="px-2 py-2 text-center font-mono text-slate-500 text-[11px]">{p.min_post_shift_gap}–{p.max_post_shift_gap}</td>
                      <td className="px-2 py-2 text-center font-mono text-slate-500">{p.shift_events}</td>
                      <td className="px-2 py-2 text-center text-[10px]">
                        {overdue ? (
                          <span className="inline-block px-1.5 py-0.5 rounded bg-red-200 text-red-800 font-black">⚠ OVERDUE</span>
                        ) : remaining <= 3 ? (
                          <span className="inline-block px-1.5 py-0.5 rounded bg-amber-200 text-amber-800 font-bold">~{remaining} draws</span>
                        ) : (
                          <span className="inline-block px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold">~{remaining} draws</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-[10px] text-slate-600 italic leading-relaxed">
            <b>Reading:</b> When a number with H position at <i>Ball X</i> currently has its last appearance at a different (non-H) ball, the <b>Avg H-Return</b> column shows historically how many draws it took to return to its H-ball. <b className="text-red-600">⚠ OVERDUE</b> = current shift gap already exceeds the historical average — return is statistically imminent.
          </div>
        </div>
      )}
    </div>
  );
}







function ClassificationTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reversion, setReversion] = useState(null);

  useEffect(() => {
    axios.get(`${API}/analysis/hml-classification`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => { toast.error('Failed to load classification'); setLoading(false); });
    axios.get(`${API}/analysis/reversion-analyzer`)
      .then(r => { setReversion(r.data); })
      .catch(() => {});
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;
  if (!data) return null;

  const ballLabels = {'ball_1': 'B1', 'ball_2': 'B2', 'ball_3': 'B3', 'ball_4': 'B4', 'ball_5': 'B5'};
  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
  const tagColors = {
    'H5': 'bg-red-600 text-white', 'H4': 'bg-red-500 text-white', 'H3': 'bg-orange-500 text-white',
    'H2': 'bg-amber-500 text-white', 'H1': 'bg-green-600 text-white',
    'M-only': 'bg-blue-500 text-white', 'L-only': 'bg-gray-400 text-white',
  };
  const tierBg = { 'H': 'bg-green-100 text-green-800', 'M': 'bg-amber-100 text-amber-800', 'L': 'bg-red-50 text-red-400' };

  return (
    <div className="space-y-6" data-testid="classification-tab">
      {/* Rule explanation */}
      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-4">
        <h3 className="font-bold text-indigo-900 mb-1">Block 1 — Coverage-Based HML Classification (50/30/20)</h3>
        <p className="text-xs text-indigo-700">For each ball position, numbers are sorted by appearance frequency. <strong>H</strong> = top numbers covering first 50% of appearances, <strong>M</strong> = next 30%, <strong>L</strong> = remaining 20%. Position profiles show how many positions each number is H at.</p>
      </div>

      {/* Position Summary */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-gray-200 bg-gray-800 text-white">
          <h3 className="font-bold">Block 2 — Per-Position Tier Summary</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-bold text-gray-500">Position</th>
                <th className="px-3 py-2 text-center font-bold text-green-700">H Count</th>
                <th className="px-3 py-2 text-center font-bold text-green-700">H Coverage</th>
                <th className="px-3 py-2 text-center font-bold text-amber-700">M Count</th>
                <th className="px-3 py-2 text-center font-bold text-amber-700">M Coverage</th>
                <th className="px-3 py-2 text-center font-bold text-red-700">L Count</th>
                <th className="px-3 py-2 text-center font-bold text-red-700">L Coverage</th>
              </tr>
            </thead>
            <tbody>
              {ballKeys.map(bk => {
                const ps = data.position_summary[bk];
                return (
                  <tr key={bk} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2.5 font-bold text-gray-900">{ps.label}</td>
                    <td className="px-3 py-2.5 text-center font-bold text-green-700">{ps.h_count}</td>
                    <td className="px-3 py-2.5 text-center font-mono text-green-600">{ps.h_coverage}%</td>
                    <td className="px-3 py-2.5 text-center font-bold text-amber-700">{ps.m_count}</td>
                    <td className="px-3 py-2.5 text-center font-mono text-amber-600">{ps.m_coverage}%</td>
                    <td className="px-3 py-2.5 text-center font-bold text-red-700">{ps.l_count}</td>
                    <td className="px-3 py-2.5 text-center font-mono text-red-600">{ps.l_coverage}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tag Summary */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-gray-200 bg-gray-800 text-white">
          <h3 className="font-bold">Block 3 — Position Profile Tags</h3>
          <p className="text-[10px] text-gray-300 mt-0.5">H2 = H in 2 positions (Dual), H1 = H in 1 position (Positional), M-only = never H anywhere</p>
        </div>
        <div className="p-4 space-y-3">
          {data.tag_summary.map(t => (
            <div key={t.tag} className="flex items-start gap-3">
              <span className={`px-3 py-1 rounded font-bold text-sm shrink-0 min-w-[60px] text-center ${tagColors[t.tag] || 'bg-gray-300 text-white'}`}>{t.tag}</span>
              <div className="flex-1">
                <span className="text-xs font-bold text-gray-600">{t.count} numbers</span>
                <div className="flex gap-1 flex-wrap mt-1">
                  {t.numbers.map(n => (
                    <span key={n} className="inline-flex w-7 h-7 rounded-full items-center justify-center font-mono font-bold text-[10px] bg-gray-100 text-gray-700 border border-gray-200">{n}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Full Number Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="p-3 border-b border-gray-200 bg-gray-800 text-white">
          <h3 className="font-bold">Block 4 — All 50 Numbers — Position Profile</h3>
          <p className="text-[10px] text-gray-300 mt-0.5">Each number's HML tier at each ball position + overall tag</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-2 py-2 text-center font-bold text-gray-500">#</th>
                <th className="px-2 py-2 text-center font-bold text-gray-500">Tag</th>
                <th className="px-2 py-2 text-center font-bold text-gray-500">H</th>
                <th className="px-2 py-2 text-center font-bold text-gray-500">M</th>
                <th className="px-2 py-2 text-center font-bold text-gray-500">L</th>
                {ballKeys.map(bk => (
                  <th key={bk} className="px-2 py-2 text-center font-bold text-gray-500">{ballLabels[bk]}</th>
                ))}
                <th className="px-2 py-2 text-center font-bold text-indigo-600 border-l-2 border-indigo-200 bg-indigo-50" title="W46 Reversion state">State</th>
                <th className="px-2 py-2 text-center font-bold text-indigo-600 bg-indigo-50" title="Current gap × / avg gap">Overdue×</th>
                <th className="px-2 py-2 text-center font-bold text-indigo-600 bg-indigo-50" title="Affinity companions fired in last 3 draws">Triggers</th>
              </tr>
            </thead>
            <tbody>
              {data.numbers.map((n, idx) => {
                const rev = (reversion?.number_reversion || []).find(r => r.number === n.number);
                return (
                <tr key={n.number} className={`border-b border-gray-100 ${n.h_count >= 2 ? 'bg-amber-50/50' : idx % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                  <td className="px-2 py-1.5 text-center font-bold">{n.number}</td>
                  <td className="px-2 py-1.5 text-center">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${tagColors[n.tag] || 'bg-gray-300 text-white'}`}>{n.tag}</span>
                  </td>
                  <td className="px-2 py-1.5 text-center font-bold text-green-700">{n.h_count}</td>
                  <td className="px-2 py-1.5 text-center font-bold text-amber-700">{n.m_count}</td>
                  <td className="px-2 py-1.5 text-center font-bold text-red-600">{n.l_count}</td>
                  {ballKeys.map(bk => {
                    const tier = n.per_pos[bk];
                    const pct = n.pcts?.[bk] || 0;
                    return (
                      <td key={bk} className={`px-2 py-1.5 text-center ${tierBg[tier]}`}>
                        <span className="font-bold">{tier}</span>
                        <span className="text-[9px] ml-0.5 opacity-60">{pct}%</span>
                      </td>
                    );
                  })}
                  <td className="px-2 py-1.5 text-center border-l-2 border-indigo-100 bg-indigo-50/30">
                    {rev ? (
                      rev.state === 'HOT' ? <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 text-[10px] font-bold">🔥 HOT</span> :
                      rev.state === 'COLD' ? <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-bold">🧊 COLD</span> :
                      <span className="text-gray-400 text-[10px]">—</span>
                    ) : <span className="text-gray-300 text-[10px]">—</span>}
                  </td>
                  <td className="px-2 py-1.5 text-center bg-indigo-50/30 font-mono text-[10px]">
                    {rev ? (
                      <span className={rev.overdue_score >= 1.5 ? 'text-rose-700 font-bold' : 'text-gray-500'}>{rev.overdue_score}×</span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-2 py-1.5 text-center bg-indigo-50/30">
                    {rev ? (
                      rev.n_triggers_fired >= 2 ? <span className="text-amber-700 font-bold text-[10px]">🔥 {rev.n_triggers_fired}/5</span> :
                      rev.n_triggers_fired === 1 ? <span className="text-gray-600 text-[10px]">⚡ 1/5</span> :
                      <span className="text-gray-300 text-[10px]">—</span>
                    ) : <span className="text-gray-300 text-[10px]">—</span>}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ConstraintsTab() {
  const [activeSet, setActiveSet] = useState('C1');
  const [hmlSets, setHmlSets] = useState({
    C1: { pattern: [null, null, null, null, null], remarks: { criteria: '', basedOn: '' } },
    C2: { pattern: [null, null, null, null, null], remarks: { criteria: '', basedOn: '' } },
    C3: { pattern: [null, null, null, null, null], remarks: { criteria: '', basedOn: '' } },
    C4: { pattern: [null, null, null, null, null], remarks: { criteria: '', basedOn: '' } },
    C5: { pattern: [null, null, null, null, null], remarks: { criteria: '', basedOn: '' } },
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [drawRef, setDrawRef] = useState(null);
  const [isStale, setIsStale] = useState(false);

  // Load persisted constraints on mount
  useEffect(() => {
    axios.get(`${API}/analysis/constraint-sets`)
      .then(res => {
        const d = res.data;
        setDrawRef(d.draw_reference);
        setIsStale(d.is_stale);
        const newSets = { ...hmlSets };
        ['C1', 'C2', 'C3', 'C4', 'C5'].forEach(key => {
          if (d[key]) {
            const pat = d[key].hml_pattern;
            const arr = (pat && pat.length === 5) ? pat.split('') : [null, null, null, null, null];
            newSets[key] = {
              pattern: arr,
              remarks: {
                criteria: d[key].remarks_criteria || '',
                basedOn: d[key].remarks_based_on || '',
              },
            };
          }
        });
        setHmlSets(newSets);
        setLoaded(true);
      })
      .catch(() => { setLoaded(true); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTierForPos = (setKey, posIdx, tier) => {
    setHmlSets(prev => {
      const updated = { ...prev };
      const newPattern = [...updated[setKey].pattern];
      // Toggle off if clicking the already-active tier
      newPattern[posIdx] = newPattern[posIdx] === tier ? null : tier;
      updated[setKey] = { ...updated[setKey], pattern: newPattern };
      return updated;
    });
  };

  const clearSet = (setKey) => {
    setHmlSets(prev => ({
      ...prev,
      [setKey]: { pattern: [null, null, null, null, null], remarks: { criteria: '', basedOn: '' } },
    }));
  };

  const setRemark = (setKey, field, value) => {
    setHmlSets(prev => ({
      ...prev,
      [setKey]: { ...prev[setKey], remarks: { ...prev[setKey].remarks, [field]: value } },
    }));
  };

  // Use PATCH to update only HML fields without wiping other constraints
  const saveConstraints = async () => {
    setSaving(true);
    try {
      for (const key of ['C1', 'C2', 'C3', 'C4', 'C5']) {
        const pat = hmlSets[key].pattern;
        const complete = pat.every(t => t === 'H' || t === 'M' || t === 'L');
        await axios.patch(`${API}/analysis/constraint-sets`, {
          set_key: key,
          fields: {
            hml_pattern: complete ? pat.join('') : null,
            remarks_criteria: hmlSets[key].remarks.criteria || '',
            remarks_based_on: hmlSets[key].remarks.basedOn || '',
          },
        });
      }
      setIsStale(false);
      toast.success('HML constraints saved — other constraints preserved');
    } catch {
      toast.error('Failed to save constraints');
    }
    setSaving(false);
  };

  const clearAllConstraints = async () => {
    setSaving(true);
    try {
      for (const key of ['C1', 'C2', 'C3', 'C4', 'C5']) {
        await axios.patch(`${API}/analysis/constraint-sets`, {
          set_key: key,
          fields: { hml_pattern: null, remarks_criteria: '', remarks_based_on: '' },
        });
      }
      setHmlSets(prev => {
        const updated = { ...prev };
        for (const key of ['C1', 'C2', 'C3', 'C4', 'C5']) {
          updated[key] = { pattern: [null, null, null, null, null], remarks: { criteria: '', basedOn: '' } };
        }
        return updated;
      });
      toast.success('All HML constraints cleared');
    } catch {
      toast.error('Failed to clear constraints');
    }
    setSaving(false);
  };

  const removeActiveSet = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/analysis/constraint-sets`, {
        set_key: activeSet,
        fields: { hml_pattern: null, remarks_criteria: '', remarks_based_on: '' },
      });
      clearSet(activeSet);
      toast.success(`${activeSet} cleared`);
    } catch {
      toast.error(`Failed to clear ${activeSet}`);
    }
    setSaving(false);
  };

  const ballLabelsLong = ['Ball 1', 'Ball 2', 'Ball 3', 'Ball 4', 'Ball 5'];

  return (
    <div className="space-y-4" data-testid="constraints-tab">
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="border-b border-gray-200 bg-gray-800 p-3 flex items-center gap-3 flex-wrap">
          {['C1', 'C2', 'C3', 'C4', 'C5'].map(key => (
            <button key={key} onClick={() => setActiveSet(key)}
              className={`px-5 py-2.5 rounded font-bold text-sm transition ${activeSet === key ? 'bg-blue-600 text-white shadow-lg' : 'bg-gray-600 text-gray-300 hover:bg-gray-500'}`}
              data-testid={`tab-${key}`}>{key}</button>
          ))}
          <span className="text-xs text-gray-400 ml-2">5 independent HML constraint sets — generation in Pattern Predictor</span>
          {isStale && <span className="ml-auto text-xs font-bold text-amber-400 bg-amber-900/30 px-2 py-1 rounded">New draw added — constraints may be stale</span>}
        </div>

        {/* C1-C5 overview strip */}
        <div className="flex flex-wrap gap-2 bg-gray-700 px-4 py-2.5 border-b border-gray-600">
          {['C1','C2','C3','C4','C5'].map(key => {
            const arr = hmlSets[key].pattern;
            const hasVal = arr.some(t => t === 'H' || t === 'M' || t === 'L');
            return (
              <div key={key} className={`flex items-center gap-1.5 text-xs ${activeSet === key ? 'ring-1 ring-white/40 rounded px-1.5 py-0.5 bg-white/10' : ''}`}>
                <span className={`font-bold ${hasVal ? 'text-emerald-300' : 'text-white/30'}`}>{key}:</span>
                {hasVal ? (
                  <span className="flex gap-0.5">
                    {arr.map((t, ti) => (
                      <span key={ti} className={`w-5 h-5 rounded text-[10px] font-bold flex items-center justify-center ${
                        t === 'H' ? 'bg-green-500/80 text-white' :
                        t === 'M' ? 'bg-amber-500/80 text-white' :
                        t === 'L' ? 'bg-red-500/80 text-white' :
                        'bg-white/10 text-white/40 border border-dashed border-white/30'
                      }`}>{t || '—'}</span>
                    ))}
                  </span>
                ) : (
                  <span className="text-white/30 italic">not set</span>
                )}
              </div>
            );
          })}
        </div>
        {['C1', 'C2', 'C3', 'C4', 'C5'].map(key => {
          if (key !== activeSet) return null;
          const sd = hmlSets[key];
          return (
            <div key={key} className="p-4 space-y-4">
              <p className="text-xs text-gray-500">Select H/M/L tier for each ball position. Save to persist — tickets are generated in Pattern Predictor.</p>
              <div className="grid grid-cols-5 gap-3">
                {ballLabelsLong.map((label, i) => (
                  <div key={i} className="text-center">
                    <p className="text-[10px] text-gray-500 uppercase font-bold mb-2">{label}</p>
                    <div className="flex gap-1 justify-center">
                      {['H', 'M', 'L'].map(tier => {
                        const isActive = sd.pattern[i] === tier;
                        const color = tier === 'H' ? (isActive ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 border border-green-200')
                          : tier === 'M' ? (isActive ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 border border-amber-200')
                          : (isActive ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 border border-red-200');
                        return (
                          <button key={tier} onClick={() => setTierForPos(key, i, tier)}
                            className={`w-10 h-10 rounded-lg font-bold text-sm transition ${color}`}
                            data-testid={`${key}-pos${i}-${tier}`}>{tier}</button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Remarks */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2" data-testid={`${key}-remarks`}>
                <p className="text-[10px] font-bold text-blue-800 uppercase">Remarks — Why this pattern?</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 block mb-1">Criteria</label>
                    <input
                      type="text"
                      value={sd.remarks.criteria}
                      onChange={e => setRemark(key, 'criteria', e.target.value)}
                      placeholder="e.g. Top Occurrence, Lacking, Overdue..."
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
                      data-testid={`${key}-criteria`}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-gray-500 block mb-1">Based on</label>
                    <input
                      type="text"
                      value={sd.remarks.basedOn}
                      onChange={e => setRemark(key, 'basedOn', e.target.value)}
                      placeholder="e.g. Pattern Comparison — Historical vs 2026"
                      className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
                      data-testid={`${key}-basedOn`}
                    />
                  </div>
                </div>
                <div className="overflow-x-auto mt-1">
                  <table className="w-full text-[10px]">
                    <thead>
                      <tr className="bg-blue-100/50">
                        <th className="px-2 py-1 text-left text-blue-800">S.No</th>
                        <th className="px-2 py-1 text-left text-blue-800">Criteria</th>
                        <th className="px-2 py-1 text-left text-blue-800">Based on</th>
                        {ballLabelsLong.map((l, i) => (
                          <th key={i} className="px-2 py-1 text-center text-blue-800">{l}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-blue-200">
                        <td className="px-2 py-1.5 font-bold text-gray-700">{key.replace('C', '')}</td>
                        <td className="px-2 py-1.5 text-gray-700">{sd.remarks.criteria || <span className="text-gray-300 italic">—</span>}</td>
                        <td className="px-2 py-1.5 text-gray-700">{sd.remarks.basedOn || <span className="text-gray-300 italic">—</span>}</td>
                        {sd.pattern.map((tier, i) => {
                          const tierColor = tier === 'H' ? 'bg-green-100 text-green-800' : tier === 'M' ? 'bg-amber-100 text-amber-800' : tier === 'L' ? 'bg-red-100 text-red-800' : 'bg-gray-50 text-gray-300 border border-dashed border-gray-300';
                          return <td key={i} className={`px-2 py-1.5 text-center font-bold ${tierColor}`}>{tier || '—'}</td>;
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border border-gray-200">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-500">{key} Pattern:</span>
                  <PatternBadge pattern={sd.pattern.map(c => c || '·').join('')} />
                </div>
                <button onClick={saveConstraints} disabled={saving}
                  className="px-5 py-2.5 bg-[#002FA7] hover:bg-[#001d6a] text-white rounded-lg font-bold text-sm transition disabled:opacity-50 flex items-center gap-2"
                  data-testid={`save-constraints-btn`}>
                  {saving ? <CircleNotch size={16} className="animate-spin" /> : null}
                  Save All Constraints
                </button>
                <button onClick={removeActiveSet} disabled={saving}
                  className="px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-bold text-sm transition disabled:opacity-50 flex items-center gap-2"
                  data-testid={`remove-active-btn`}>
                  Remove {activeSet}
                </button>
                <button onClick={clearAllConstraints} disabled={saving}
                  className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-bold text-sm transition disabled:opacity-50 flex items-center gap-2"
                  data-testid="clear-all-constraints-btn">
                  Remove All
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary of all 3 sets */}
      {loaded && (
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="constraints-summary">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Block 1 — All Constraint Sets Summary</h3>
          <div className="grid grid-cols-3 gap-3">
            {['C1', 'C2', 'C3', 'C4', 'C5'].map(key => (
              <div key={key} className={`rounded-lg p-3 border ${activeSet === key ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-sm text-gray-800">{key}</span>
                  <PatternBadge pattern={hmlSets[key].pattern.map(c => c || '·').join('')} />
                </div>
                {hmlSets[key].remarks.criteria && (
                  <p className="text-[10px] text-gray-500 truncate">{hmlSets[key].remarks.criteria}</p>
                )}
              </div>
            ))}
          </div>
          {drawRef && <p className="text-[10px] text-gray-400 mt-2">Saved for draw #{drawRef}</p>}
        </div>
      )}
    </div>
  );
}

const StatusBadgePattern = ({ status }) => {
  if (status === 'UNDER') return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700">LACKING</span>;
  if (status === 'OVER') return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700">OVER</span>;
  return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 text-gray-500">OK</span>;
};

function HMLPatternsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [patternSort, setPatternSort] = useState({ key: 'hist_count', dir: 'desc' });

  useEffect(() => {
    axios.get(`${API}/analysis/number-appearance/hml-patterns`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => { toast.error('Failed to load HML patterns'); setLoading(false); });
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;
  if (!data) return <div className="text-center py-10 text-gray-500">Failed to load data</div>;

  const ballLabels = ['B1', 'B2', 'B3', 'B4', 'B5'];
  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];

  return (
    <div className="space-y-6" data-testid="hml-patterns-tab">
      {/* Historical vs 2026 Tier Appearance % */}
      <div className="bg-white border border-indigo-200 rounded-lg overflow-hidden" data-testid="tier-comparison-section">
        <div className="p-4 border-b border-indigo-200 bg-indigo-50">
          <h3 className="font-bold text-indigo-900">Block 1 — Tier Appearance % — Historical (till {data.current_year - 1}) vs {data.current_year}</h3>
          <p className="text-xs text-indigo-600 mt-1">When a ball is drawn, what % of the time is it an H, M, or L number? Compares all-time historical rate with {data.current_year}. Negative diff = lacking.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="tier-comparison-table">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left font-bold text-gray-500" rowSpan={2}>Pos</th>
                <th className="px-3 py-2 text-center font-bold text-green-700 border-b border-green-200" colSpan={3}>High (H)</th>
                <th className="px-3 py-2 text-center font-bold text-amber-700 border-b border-amber-200" colSpan={3}>Medium (M)</th>
                <th className="px-3 py-2 text-center font-bold text-red-700 border-b border-red-200" colSpan={3}>Low (L)</th>
              </tr>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-2 py-1 text-center text-[10px] text-gray-500">Hist%</th>
                <th className="px-2 py-1 text-center text-[10px] text-indigo-600">{data.current_year}%</th>
                <th className="px-2 py-1 text-center text-[10px] text-gray-500">Diff</th>
                <th className="px-2 py-1 text-center text-[10px] text-gray-500">Hist%</th>
                <th className="px-2 py-1 text-center text-[10px] text-indigo-600">{data.current_year}%</th>
                <th className="px-2 py-1 text-center text-[10px] text-gray-500">Diff</th>
                <th className="px-2 py-1 text-center text-[10px] text-gray-500">Hist%</th>
                <th className="px-2 py-1 text-center text-[10px] text-indigo-600">{data.current_year}%</th>
                <th className="px-2 py-1 text-center text-[10px] text-gray-500">Diff</th>
              </tr>
            </thead>
            <tbody>
              {ballKeys.map((bk, i) => {
                const pc = data.position_tier_comparison?.[bk] || {};
                const diffColor = (v) => v > 2 ? 'text-blue-600 font-bold' : v < -2 ? 'text-red-600 font-bold' : 'text-gray-500';
                const diffBg = (v) => v < -3 ? 'bg-red-50' : v > 3 ? 'bg-blue-50' : '';
                return (
                  <tr key={bk} className={`border-b border-gray-100 hover:bg-gray-50`}>
                    <td className="px-3 py-3 font-bold text-gray-900">{ballLabels[i]}</td>
                    <td className="px-2 py-3 text-center font-mono text-green-700">{pc.h_hist_pct}%</td>
                    <td className="px-2 py-3 text-center font-mono font-bold text-indigo-700">{pc.h_cy_pct}%</td>
                    <td className={`px-2 py-3 text-center font-mono ${diffColor(pc.h_diff)} ${diffBg(pc.h_diff)}`}>{pc.h_diff > 0 ? '+' : ''}{pc.h_diff}%</td>
                    <td className="px-2 py-3 text-center font-mono text-amber-700">{pc.m_hist_pct}%</td>
                    <td className="px-2 py-3 text-center font-mono font-bold text-indigo-700">{pc.m_cy_pct}%</td>
                    <td className={`px-2 py-3 text-center font-mono ${diffColor(pc.m_diff)} ${diffBg(pc.m_diff)}`}>{pc.m_diff > 0 ? '+' : ''}{pc.m_diff}%</td>
                    <td className="px-2 py-3 text-center font-mono text-red-700">{pc.l_hist_pct}%</td>
                    <td className="px-2 py-3 text-center font-mono font-bold text-indigo-700">{pc.l_cy_pct}%</td>
                    <td className={`px-2 py-3 text-center font-mono ${diffColor(pc.l_diff)} ${diffBg(pc.l_diff)}`}>{pc.l_diff > 0 ? '+' : ''}{pc.l_diff}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Annual Tier Breakdown */}
      {data.annual_tier_breakdown && data.annual_tier_breakdown.length > 0 && (
        <div id="block2-annual-tier" className="bg-white border border-gray-200 rounded-lg overflow-hidden scroll-mt-20" data-testid="annual-breakdown-section">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-bold text-gray-900">Block 2 — Annual Tier % Breakdown</h3>
            <p className="text-xs text-gray-500 mt-1">Year-by-year H/M/L appearance % per position. Compare {data.current_year} against recent years to spot deviations.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="annual-breakdown-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left font-bold text-gray-500">Year</th>
                  <th className="px-3 py-2 text-center font-bold text-gray-500">Draws</th>
                  {ballLabels.map((bl) => (
                    <th key={bl} className="px-2 py-2 text-center font-bold text-gray-500" colSpan={3}>{bl}</th>
                  ))}
                </tr>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-1"></th>
                  <th className="px-3 py-1"></th>
                  {ballLabels.map((bl) => (
                    <React.Fragment key={bl}>
                      <th className="px-1 py-1 text-center text-[10px] text-green-600">H%</th>
                      <th className="px-1 py-1 text-center text-[10px] text-amber-600">M%</th>
                      <th className="px-1 py-1 text-center text-[10px] text-red-600">L%</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const cy = data.current_year;
                  const rows = data.annual_tier_breakdown || [];
                  const histRows = rows.filter(y => y.year >= cy - 5 && y.year < cy);
                  const cyRow = rows.find(y => y.year === cy);
                  // Compute averages per ball
                  const avgs = {};
                  ballKeys.forEach(bk => {
                    const h = histRows.map(y => y.positions?.[bk]?.h_pct || 0);
                    const m = histRows.map(y => y.positions?.[bk]?.m_pct || 0);
                    const l = histRows.map(y => y.positions?.[bk]?.l_pct || 0);
                    const n = Math.max(1, h.length);
                    avgs[bk] = {
                      h: +(h.reduce((a, b) => a + b, 0) / n).toFixed(1),
                      m: +(m.reduce((a, b) => a + b, 0) / n).toFixed(1),
                      l: +(l.reduce((a, b) => a + b, 0) / n).toFixed(1),
                    };
                  });
                  const totalHistDraws = histRows.reduce((a, b) => a + (b.draws || 0), 0);
                  const yearRange = histRows.length > 0 ? `${histRows[0].year}-${histRows[histRows.length - 1].year}` : '';
                  const cellColor = (v) => v > 2 ? 'text-emerald-700 bg-emerald-50' : v < -2 ? 'text-rose-700 bg-rose-50' : 'text-gray-600';
                  const fmtD = (v) => (v > 0 ? `+${v}` : `${v}`) + '%';

                  return (
                    <>
                      {/* Historical year rows (excluding current year) */}
                      {histRows.map((yr) => (
                        <tr key={yr.year} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2.5 font-bold text-gray-900">{yr.year}</td>
                          <td className="px-3 py-2.5 text-center font-mono text-gray-500">{yr.draws}</td>
                          {ballKeys.map((bk) => {
                            const p = yr.positions?.[bk] || {};
                            return (
                              <React.Fragment key={bk}>
                                <td className="px-1 py-2.5 text-center font-mono text-green-700">{p.h_pct}%</td>
                                <td className="px-1 py-2.5 text-center font-mono text-amber-700">{p.m_pct}%</td>
                                <td className="px-1 py-2.5 text-center font-mono text-red-700">{p.l_pct}%</td>
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      ))}
                      {/* Also render any years outside the recent 5-year range (older) */}
                      {rows.filter(y => y.year < cy - 5).map((yr) => (
                        <tr key={yr.year} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2.5 font-bold text-gray-700">{yr.year}</td>
                          <td className="px-3 py-2.5 text-center font-mono text-gray-400">{yr.draws}</td>
                          {ballKeys.map((bk) => {
                            const p = yr.positions?.[bk] || {};
                            return (
                              <React.Fragment key={bk}>
                                <td className="px-1 py-2.5 text-center font-mono text-green-600/70">{p.h_pct}%</td>
                                <td className="px-1 py-2.5 text-center font-mono text-amber-600/70">{p.m_pct}%</td>
                                <td className="px-1 py-2.5 text-center font-mono text-red-600/70">{p.l_pct}%</td>
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      ))}
                      {/* Average of last 5 years */}
                      {histRows.length > 0 && (
                        <tr className="border-b-2 border-slate-400 bg-slate-100 font-bold" data-testid="avg-row">
                          <td className="px-3 py-2.5 font-bold text-slate-900">Avg {yearRange}</td>
                          <td className="px-3 py-2.5 text-center font-mono text-slate-600">{totalHistDraws}</td>
                          {ballKeys.map((bk) => (
                            <React.Fragment key={bk}>
                              <td className="px-1 py-2.5 text-center font-mono text-green-800">{avgs[bk].h}%</td>
                              <td className="px-1 py-2.5 text-center font-mono text-amber-800">{avgs[bk].m}%</td>
                              <td className="px-1 py-2.5 text-center font-mono text-red-800">{avgs[bk].l}%</td>
                            </React.Fragment>
                          ))}
                        </tr>
                      )}
                      {/* Current year (2026) */}
                      {cyRow && (
                        <tr className="border-b border-indigo-200 bg-indigo-50 font-bold">
                          <td className="px-3 py-2.5 font-bold text-indigo-700">{cy}</td>
                          <td className="px-3 py-2.5 text-center font-mono text-indigo-500">{cyRow.draws}</td>
                          {ballKeys.map((bk) => {
                            const p = cyRow.positions?.[bk] || {};
                            return (
                              <React.Fragment key={bk}>
                                <td className="px-1 py-2.5 text-center font-mono text-green-700">{p.h_pct}%</td>
                                <td className="px-1 py-2.5 text-center font-mono text-amber-700">{p.m_pct}%</td>
                                <td className="px-1 py-2.5 text-center font-mono text-red-700">{p.l_pct}%</td>
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      )}
                      {/* Diff row: 2026 − Avg (last 5 years) */}
                      {cyRow && histRows.length > 0 && (
                        <tr className="border-b border-amber-300 bg-amber-50 font-bold" data-testid="diff-row">
                          <td className="px-3 py-2.5 font-bold text-amber-900">Diff ({cy} − Avg)</td>
                          <td className="px-3 py-2.5 text-center font-mono text-amber-700">—</td>
                          {ballKeys.map((bk) => {
                            const p = cyRow.positions?.[bk] || {};
                            const hD = +((p.h_pct || 0) - avgs[bk].h).toFixed(1);
                            const mD = +((p.m_pct || 0) - avgs[bk].m).toFixed(1);
                            const lD = +((p.l_pct || 0) - avgs[bk].l).toFixed(1);
                            return (
                              <React.Fragment key={bk}>
                                <td className={`px-1 py-2.5 text-center font-mono ${cellColor(hD)}`}>{fmtD(hD)}</td>
                                <td className={`px-1 py-2.5 text-center font-mono ${cellColor(mD)}`}>{fmtD(mD)}</td>
                                <td className={`px-1 py-2.5 text-center font-mono ${cellColor(lD)}`}>{fmtD(lD)}</td>
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      )}
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 2026 Draw-by-Draw Patterns */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">Block 3 — {data.current_year} Draw Patterns</h3>
            <p className="text-xs text-gray-500 mt-1">{data.total_cy} draws — each ball classified as H/M/L based on its position frequency tier</p>
          </div>
          <span className="text-xs text-gray-400 font-mono">{data.total_cy} draws</span>
        </div>
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="cy-draws-table">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left font-bold text-gray-500">Draw #</th>
                <th className="px-3 py-2 text-left font-bold text-gray-500">Date</th>
                <th className="px-3 py-2 text-center font-bold text-gray-500">B1</th>
                <th className="px-3 py-2 text-center font-bold text-gray-500">B2</th>
                <th className="px-3 py-2 text-center font-bold text-gray-500">B3</th>
                <th className="px-3 py-2 text-center font-bold text-gray-500">B4</th>
                <th className="px-3 py-2 text-center font-bold text-gray-500">B5</th>
                <th className="px-3 py-2 text-center font-bold text-gray-500">Pattern</th>
              </tr>
            </thead>
            <tbody>
              {data.cy_draws.map((d) => (
                <tr key={d.draw_number} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`draw-row-${d.draw_number}`}>
                  <td className="px-3 py-2 font-mono font-bold text-gray-900">{d.draw_number}</td>
                  <td className="px-3 py-2 text-gray-600">{d.draw_date}</td>
                  {d.details.map((det, j) => {
                    const tierColor = det.tier === 'H' ? 'bg-green-50 text-green-800 font-bold' : det.tier === 'M' ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-800';
                    return (
                      <td key={j} className={`px-3 py-2 text-center font-mono ${tierColor}`}>
                        {det.number} <span className="text-[10px] opacity-60">({det.tier})</span>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-center"><PatternBadge pattern={d.pattern} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pattern Comparison: Historical vs 2026 */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900">Block 4 — Pattern Comparison — Historical vs {data.current_year}</h3>
            <p className="text-xs text-gray-500 mt-1">How often each HML pattern appeared historically ({data.total_historical} draws) vs {data.current_year} ({data.total_cy} draws). Diff = {data.current_year}% - Historical%</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowAll(!showAll)} className="text-xs">
            {showAll ? 'Show Top 20' : 'Show All'}
          </Button>
        </div>
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="comparison-table">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-2 text-left font-bold text-gray-500 cursor-pointer select-none" onClick={() => setPatternSort(s => ({ key: 'pattern', dir: s.key === 'pattern' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Pattern {patternSort.key === 'pattern' ? (patternSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                <th className="px-4 py-2 text-right font-bold text-gray-500 cursor-pointer select-none" onClick={() => setPatternSort(s => ({ key: 'hist_count', dir: s.key === 'hist_count' && s.dir === 'desc' ? 'asc' : 'desc' }))}>Hist Count {patternSort.key === 'hist_count' ? (patternSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                <th className="px-4 py-2 text-right font-bold text-gray-500 cursor-pointer select-none" onClick={() => setPatternSort(s => ({ key: 'hist_pct', dir: s.key === 'hist_pct' && s.dir === 'desc' ? 'asc' : 'desc' }))}>Hist % {patternSort.key === 'hist_pct' ? (patternSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                <th className="px-4 py-2 text-right font-bold text-indigo-600 cursor-pointer select-none" onClick={() => setPatternSort(s => ({ key: 'cy_count', dir: s.key === 'cy_count' && s.dir === 'desc' ? 'asc' : 'desc' }))}>{data.current_year} Count {patternSort.key === 'cy_count' ? (patternSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                <th className="px-4 py-2 text-right font-bold text-indigo-600 cursor-pointer select-none" onClick={() => setPatternSort(s => ({ key: 'cy_pct', dir: s.key === 'cy_pct' && s.dir === 'desc' ? 'asc' : 'desc' }))}>{data.current_year} % {patternSort.key === 'cy_pct' ? (patternSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                <th className="px-4 py-2 text-right font-bold text-gray-500 cursor-pointer select-none" onClick={() => setPatternSort(s => ({ key: 'diff', dir: s.key === 'diff' && s.dir === 'desc' ? 'asc' : 'desc' }))}>Diff {patternSort.key === 'diff' ? (patternSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
                <th className="px-4 py-2 text-center font-bold text-gray-500 cursor-pointer select-none" onClick={() => setPatternSort(s => ({ key: 'status', dir: s.key === 'status' && s.dir === 'asc' ? 'desc' : 'asc' }))}>Status {patternSort.key === 'status' ? (patternSort.dir === 'asc' ? '▲' : '▼') : ''}</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const rows = showAll ? [...data.comparison] : [...data.top_patterns];
                const statusOrder = { 'UNDER': 0, 'OK': 1, 'OVER': 2 };
                rows.sort((a, b) => {
                  let av, bv;
                  if (patternSort.key === 'status') {
                    av = statusOrder[a.status] ?? 1;
                    bv = statusOrder[b.status] ?? 1;
                  } else if (patternSort.key === 'pattern') {
                    av = a.pattern;
                    bv = b.pattern;
                  } else if (patternSort.key === 'diff') {
                    av = parseFloat(a.diff) || 0;
                    bv = parseFloat(b.diff) || 0;
                  } else {
                    av = parseFloat(a[patternSort.key]) || 0;
                    bv = parseFloat(b[patternSort.key]) || 0;
                  }
                  if (patternSort.dir === 'asc') return av > bv ? 1 : av < bv ? -1 : 0;
                  return av < bv ? 1 : av > bv ? -1 : 0;
                });
                return rows.map(p => (
                <tr key={p.pattern} className={`border-b border-gray-100 hover:bg-gray-50 ${p.status === 'UNDER' ? 'bg-red-50/50' : p.status === 'OVER' ? 'bg-blue-50/50' : ''}`}>
                  <td className="px-4 py-2"><PatternBadge pattern={p.pattern} /></td>
                  <td className="px-4 py-2 text-right font-mono font-bold">{p.hist_count}</td>
                  <td className="px-4 py-2 text-right font-mono">{p.hist_pct}%</td>
                  <td className="px-4 py-2 text-right font-mono font-bold text-indigo-700">{p.cy_count}</td>
                  <td className="px-4 py-2 text-right font-mono text-indigo-700">{p.cy_pct}%</td>
                  <td className={`px-4 py-2 text-right font-mono font-bold ${p.diff > 0 ? 'text-blue-600' : p.diff < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                    {p.diff > 0 ? '+' : ''}{p.diff}%
                  </td>
                  <td className="px-4 py-2 text-center"><StatusBadgePattern status={p.status} /></td>
                </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lacking & Over-represented side by side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Lacking in 2026 */}
        <div className="bg-white border border-red-200 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-red-200 bg-red-50">
            <h3 className="font-bold text-red-800 flex items-center gap-2">
              <CaretDown size={16} weight="bold" />
              Block 5 — Lacking in {data.current_year}
            </h3>
            <p className="text-[11px] text-red-600 mt-0.5">Patterns appearing less in {data.current_year} than historical average — statistically "due"</p>
          </div>
          <div className="p-4 space-y-2">
            {data.lacking.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No significantly lacking patterns</p>
            ) : (
              data.lacking.map((p) => (
                <div key={p.pattern} className="flex items-center justify-between bg-red-50 rounded-lg px-3 py-2 border border-red-100" data-testid={`lacking-${p.pattern}`}>
                  <PatternBadge pattern={p.pattern} />
                  <div className="text-right">
                    <p className="text-[10px] text-gray-500">Hist: <span className="font-bold text-gray-700">{p.hist_pct}%</span> ({p.hist_count}x)</p>
                    <p className="text-[10px] text-indigo-600">{data.current_year}: <span className="font-bold">{p.cy_pct}%</span> ({p.cy_count}x)</p>
                    <p className="text-[10px] font-bold text-red-600">{p.diff}% behind</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Over-represented in 2026 */}
        <div className="bg-white border border-blue-200 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-blue-200 bg-blue-50">
            <h3 className="font-bold text-blue-800 flex items-center gap-2">
              <CaretUp size={16} weight="bold" />
              Block 6 — Over-represented in {data.current_year}
            </h3>
            <p className="text-[11px] text-blue-600 mt-0.5">Patterns appearing more in {data.current_year} than historical average</p>
          </div>
          <div className="p-4 space-y-2">
            {data.over_represented.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No significantly over-represented patterns</p>
            ) : (
              data.over_represented.map((p) => (
                <div key={p.pattern} className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2 border border-blue-100" data-testid={`over-${p.pattern}`}>
                  <PatternBadge pattern={p.pattern} />
                  <div className="text-right">
                    <p className="text-[10px] text-gray-500">Hist: <span className="font-bold text-gray-700">{p.hist_pct}%</span> ({p.hist_count}x)</p>
                    <p className="text-[10px] text-indigo-600">{data.current_year}: <span className="font-bold">{p.cy_pct}%</span> ({p.cy_count}x)</p>
                    <p className="text-[10px] font-bold text-blue-600">+{p.diff}% above</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

    </div>
  );
}

function PositionHMLTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/analysis/number-appearance/hml-patterns`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => { toast.error('Failed to load'); setLoading(false); });
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;
  if (!data) return <div className="text-center py-10 text-gray-500">Failed to load data</div>;

  const ballLabels = ['Ball 1', 'Ball 2', 'Ball 3', 'Ball 4', 'Ball 5'];
  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];
  const tierColors = {
    H: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', badge: 'bg-green-100 text-green-800' },
    M: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800', badge: 'bg-amber-100 text-amber-800' },
    L: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', badge: 'bg-red-100 text-red-800' },
  };

  return (
    <div className="space-y-6" data-testid="position-hml-tab">
      <div>
        <h2 className="text-base font-bold text-gray-900">Individual Ball Wise Analysis</h2>
        <p className="text-xs text-gray-500 mt-1">For each ball position: H/M/L appearance %, which tier is lacking in {data.current_year}, gap status, and underperforming numbers.</p>
      </div>

      {ballKeys.map((bk, bIdx) => {
        const pc = data.position_tier_comparison?.[bk] || {};
        const det = data.position_hml_detail?.[bk] || {};
        const td = data.tier_details?.[bk] || {};

        // Find most lacking tier
        const diffs = [
          { tier: 'H', diff: pc.h_diff || 0 },
          { tier: 'M', diff: pc.m_diff || 0 },
          { tier: 'L', diff: pc.l_diff || 0 },
        ];
        const mostLacking = [...diffs].sort((a, b) => a.diff - b.diff)[0];

        return (
          <div key={bk} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`pos-detail-${bk}`}>
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#002FA7] flex items-center justify-center text-white font-black text-sm">{ballLabels[bIdx].replace('Ball ', 'B')}</div>
                <div>
                  <h3 className="font-bold text-gray-900">Block {bIdx + 1} — {ballLabels[bIdx]}</h3>
                  <p className="text-[10px] text-gray-400">H: {td.h_count} nums | M: {td.m_count} nums | L: {td.l_count} nums</p>
                </div>
              </div>
              <div className={`px-3 py-1.5 rounded-lg ${tierColors[mostLacking.tier].badge} font-bold text-xs`}>
                Most Lacking: {mostLacking.tier} ({mostLacking.diff > 0 ? '+' : ''}{mostLacking.diff}%)
              </div>
            </div>

            {/* 3 tier cards */}
            <div className="grid grid-cols-3 divide-x divide-gray-200">
              {['H', 'M', 'L'].map(tier => {
                const tc = tierColors[tier];
                const d_tier = det[tier] || {};
                const histPct = tier === 'H' ? pc.h_hist_pct : tier === 'M' ? pc.m_hist_pct : pc.l_hist_pct;
                const cyPct = tier === 'H' ? pc.h_cy_pct : tier === 'M' ? pc.m_cy_pct : pc.l_cy_pct;
                const diff = tier === 'H' ? pc.h_diff : tier === 'M' ? pc.m_diff : pc.l_diff;
                const isLacking = tier === mostLacking.tier;

                return (
                  <div key={tier} className={`p-4 ${isLacking ? tc.bg : ''}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className={`px-2.5 py-1 rounded-lg font-bold text-sm ${tc.badge}`}>{tier === 'H' ? 'High' : tier === 'M' ? 'Medium' : 'Low'}</span>
                      {isLacking && <span className="text-[10px] font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded">LACKING</span>}
                    </div>

                    {/* % comparison */}
                    <div className="space-y-1.5 text-xs mb-3">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Historical %</span>
                        <span className="font-mono font-bold">{histPct}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-indigo-600">{data.current_year} %</span>
                        <span className="font-mono font-bold text-indigo-700">{cyPct}%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Diff</span>
                        <span className={`font-mono font-bold ${diff < -2 ? 'text-red-600' : diff > 2 ? 'text-blue-600' : 'text-gray-500'}`}>
                          {diff > 0 ? '+' : ''}{diff}%
                        </span>
                      </div>
                    </div>

                    {/* Streak / gap data */}
                    <div className={`rounded-lg p-2.5 ${tc.bg} border ${tc.border} space-y-1.5 text-xs`}>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Draws absent</span>
                        <span className={`font-mono font-bold ${d_tier.draws_absent >= 10 ? 'text-red-600' : d_tier.draws_absent >= 5 ? 'text-amber-600' : 'text-gray-700'}`}>
                          {d_tier.draws_absent}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Expected gap</span>
                        <span className="font-mono text-gray-700">every {d_tier.expected_gap} draws</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Gap status</span>
                        <span className={`font-mono font-bold ${d_tier.overdue_pct >= 150 ? 'text-red-600' : d_tier.overdue_pct >= 100 ? 'text-amber-600' : 'text-green-600'}`}>
                          {d_tier.overdue_pct >= 100 ? `${d_tier.overdue_pct}% overdue` : `${d_tier.overdue_pct}% of expected`}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Avg gap</span>
                        <span className="font-mono text-gray-700">{d_tier.avg_gap}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max gap</span>
                        <span className="font-mono text-gray-700">{d_tier.max_gap}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total appearances</span>
                        <span className="font-mono text-gray-700">{d_tier.total_appearances}</span>
                      </div>
                    </div>

                    {/* Last 10 draws sequence */}
                    <div className="mt-3">
                      <p className="text-[10px] text-gray-400 mb-1">Last 10 draws</p>
                      <div className="flex gap-0.5">
                        {(d_tier.last_10 || []).map((t, j) => {
                          const c = tierColors[t];
                          return <span key={j} className={`w-5 h-5 rounded text-[9px] font-bold flex items-center justify-center ${c.badge}`}>{t}</span>;
                        })}
                      </div>
                    </div>

                    {/* Performance numbers */}
                    {(() => {
                      const perf = td[`${tier.toLowerCase()}_perf`] || {};
                      const underNums = perf.underperforming_nums || [];
                      const tierNums = td[tier] || [];
                      const totalCount = perf.total || tierNums.length || 1;
                      const performingCount = totalCount - (perf.underperforming_count || 0);
                      const performingPct = Math.round((performingCount / totalCount) * 100);
                      return (
                        <div className={`mt-3 rounded-lg p-2.5 border ${isLacking ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`} data-testid={`${bk}-${tier}-perf`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[10px] font-bold text-gray-600">{data.current_year} Performance %</span>
                            <span className={`text-[10px] font-bold ${performingPct >= 70 ? 'text-green-600' : performingPct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                              {performingCount}/{totalCount} ({performingPct}%)
                            </span>
                          </div>
                          {underNums.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {tierNums.map(n => {
                                const isUnder = underNums.includes(n.number);
                                return (
                                  <span key={n.number} className={`px-1.5 py-0.5 rounded text-[10px] font-mono font-bold ${isUnder ? 'bg-red-200 text-red-800' : 'bg-white text-gray-400 border border-gray-200'}`}>
                                    {n.number}{isUnder ? <span className="text-[8px] ml-0.5">({n.cy_count}/{n.expected_so_far})</span> : ''}
                                  </span>
                                );
                              })}
                            </div>
                          ) : (
                            <p className="text-[10px] text-green-600">All numbers performing at or above expected</p>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ============ Master Sheet Tab — comprehensive per-ball table (Excel-style) ============ */
function MasterSheetTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBall, setActiveBall] = useState('ball_1');
  const [tierFilter, setTierFilter] = useState('all');
  const [awaited, setAwaited] = useState(null);
  const [awaitedLoading, setAwaitedLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/analysis/hml-rebound/master-sheet`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load master sheet'))
      .finally(() => setLoading(false));
    axios.get(`${API}/analysis/most-awaited?tolerance=medium`)
      .then(r => { setAwaited(r.data); setAwaitedLoading(false); })
      .catch(() => { setAwaitedLoading(false); });
  }, []);

  if (loading) return <div className="flex justify-center py-10"><CircleNotch size={28} className="animate-spin text-[#002FA7]" /></div>;
  if (!data) return <div className="text-gray-500 p-6">No data</div>;

  const currentBall = data.balls.find(b => b.ball === activeBall) || data.balls[0];
  const filteredRows = tierFilter === 'all' ? currentBall.rows : currentBall.rows.filter(r => r.hml === tierFilter);

  const tierBg = { H: 'bg-rose-100 text-rose-800', M: 'bg-amber-100 text-amber-800', L: 'bg-emerald-100 text-emerald-800' };
  const tierBorder = { H: 'border-l-4 border-rose-400', M: 'border-l-4 border-amber-400', L: 'border-l-4 border-emerald-400' };

  const exportCsv = () => {
    const lines = [];
    lines.push(`Ball,${currentBall.label}`);
    lines.push(['#', `Till 2025 in ${currentBall.label}`, 'AVG PR YR', 'Total in all Balls till 2025', 'Avg. PR YR (all)', `${data.next_month_name} all balls till 2025`, `${data.next_month_name} count`, `${data.next_month_name} %`, 'HML', '2026', 'Max. GAP', 'Current GAP', 'Avg Interval', 'Min Interval', 'Cycle %', 'Cycle Status', 'Recent Surface (draws ago)', 'Recent Ball', 'Avg. Return after resurface'].join(','));
    for (const r of currentBall.rows) {
      lines.push([
        r.number, r.till_2025_at_ball, r.avg_pr_yr_at_ball,
        r.total_all_balls_till_2025, r.avg_pr_yr_all_balls,
        r.next_month_count_all_balls,
        r.next_month_count, r.next_month_pct,
        r.hml || '', r.count_2026_at_ball,
        r.max_gap, r.current_gap ?? '',
        r.avg_interval ?? '', r.min_interval ?? '', r.cycle_phase ?? '', r.cycle_status || '',
        r.recent_surface_draws_ago ?? '', r.recent_surface_ball || '',
        r.avg_return_after_resurface ?? ''
      ].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `w7_master_sheet_${currentBall.ball}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Compute top-2 next-month hit values for current ball (for green-circle highlight)
  const monthCounts = currentBall.rows.map(r => r.next_month_count).filter(c => c > 0);
  const sortedDesc = [...new Set(monthCounts)].sort((a, b) => b - a);
  const top2Threshold = sortedDesc.length >= 2 ? sortedDesc[1] : (sortedDesc[0] || 0); // value of the 2nd-highest distinct count

  return (
    <div className="space-y-5" data-testid="master-sheet-tab">
      {/* MAX-CONFLUENCE PICKS — pinned at top */}
      <MostAwaitedPanel awaited={awaited} loading={awaitedLoading} />

      {/* Header */}
      <div className="bg-gradient-to-r from-[#002FA7] to-indigo-700 rounded-lg px-5 py-3 text-white flex items-center justify-between">
        <div>
          <h3 className="font-bold text-lg flex items-center gap-2">
            <span className="inline-block px-2 py-0.5 bg-amber-400 text-slate-900 rounded text-[10px] font-black">MASTER</span>
            Per-Ball Master Sheet — All Numbers × All Parameters
          </h3>
          <p className="text-[11px] text-white/90 mt-0.5">{data.total_draws} total draws · {data.years_till_2025} years analysed (till 2025) · next-draw month: <b>{data.next_month_name}</b> (auto-updates with latest draw) · all 50 numbers in one matrix per ball.</p>
        </div>
        <Button onClick={exportCsv} className="bg-white text-[#002FA7] hover:bg-amber-50 font-bold text-xs h-8" data-testid="ms-export-btn">Export CSV</Button>
      </div>

      {/* Ball selector */}
      <div className="flex gap-2 flex-wrap items-center" data-testid="ms-ball-tabs">
        {data.balls.map(b => (
          <button
            key={b.ball}
            onClick={() => setActiveBall(b.ball)}
            data-testid={`ms-ball-${b.ball}`}
            className={`px-4 py-2 rounded-lg font-bold text-sm transition-colors ${
              activeBall === b.ball
                ? 'bg-[#002FA7] text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {b.label}
          </button>
        ))}
        <div className="ml-auto flex gap-1 items-center">
          <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mr-2">Tier filter:</span>
          {['all', 'H', 'M', 'L'].map(f => (
            <button
              key={f}
              onClick={() => setTierFilter(f)}
              className={`px-2.5 py-1 rounded text-[11px] font-bold ${
                tierFilter === f
                  ? f === 'H' ? 'bg-rose-600 text-white' : f === 'M' ? 'bg-amber-600 text-white' : f === 'L' ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-white'
                  : 'bg-white border border-gray-300 text-gray-600'
              }`}
              data-testid={`ms-tier-${f}`}
            >
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>
      </div>

      {/* Master Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-slate-800 text-white px-4 py-2 flex items-center justify-between">
          <div className="font-bold text-sm">{currentBall.label} — Master Matrix ({filteredRows.length} numbers)</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid={`ms-table-${currentBall.ball}`}>
            <thead className="bg-slate-100">
              <tr>
                <th rowSpan="2" className="px-2 py-2 text-center font-black text-slate-700 border-r border-slate-300 sticky left-0 bg-slate-100">#</th>
                <th colSpan="5" className="px-2 py-1 text-center font-black text-blue-800 bg-blue-100 border-x border-slate-300">Till 2025 (history)</th>
                <th colSpan="2" className="px-2 py-1 text-center font-black text-fuchsia-800 bg-fuchsia-100 border-x border-slate-300">{data.next_month_name} (next-draw month, till 2025)</th>
                <th rowSpan="2" className="px-2 py-2 text-center font-black text-slate-700 bg-slate-200">HML</th>
                <th rowSpan="2" className="px-2 py-2 text-center font-black text-amber-800 bg-amber-100">2026<br/>{currentBall.label}</th>
                <th colSpan="2" className="px-2 py-1 text-center font-black text-rose-800 bg-rose-100 border-x border-slate-300">Gap stats</th>
                <th colSpan="2" className="px-2 py-1 text-center font-black text-indigo-800 bg-indigo-100 border-x border-slate-300">Cycle Phase</th>
                <th colSpan="3" className="px-2 py-1 text-center font-black text-violet-800 bg-violet-100 border-x border-slate-300">Cross-Ball Surface</th>
              </tr>
              <tr>
                <th className="px-2 py-2 text-center font-bold text-blue-700 bg-blue-50">Till 2025<br/>{currentBall.label}</th>
                <th className="px-2 py-2 text-center font-bold text-blue-700 bg-blue-50">AVG /YR</th>
                <th className="px-2 py-2 text-center font-bold text-blue-700 bg-blue-50">Total all balls<br/>till 2025</th>
                <th className="px-2 py-2 text-center font-bold text-blue-700 bg-blue-50">Avg /YR<br/>all balls</th>
                <th className="px-2 py-2 text-center font-bold text-sky-700 bg-sky-50">{data.next_month_name} all balls<br/>till 2025</th>
                <th className="px-2 py-2 text-center font-bold text-fuchsia-700 bg-fuchsia-50">{data.next_month_name} hits<br/>at {currentBall.label}</th>
                <th className="px-2 py-2 text-center font-bold text-fuchsia-700 bg-fuchsia-50">{data.next_month_name} %</th>
                <th className="px-2 py-2 text-center font-bold text-rose-700 bg-rose-50">Max GAP</th>
                <th className="px-2 py-2 text-center font-bold text-rose-700 bg-rose-50">Current GAP</th>
                <th className="px-2 py-2 text-center font-bold text-indigo-700 bg-indigo-50">Avg Int<br/>(min)</th>
                <th className="px-2 py-2 text-center font-bold text-indigo-700 bg-indigo-50">Cycle %<br/>Status</th>
                <th className="px-2 py-2 text-center font-bold text-violet-700 bg-violet-50">Surfaced @</th>
                <th className="px-2 py-2 text-center font-bold text-violet-700 bg-violet-50">Draws ago</th>
                <th className="px-2 py-2 text-center font-bold text-violet-700 bg-violet-50">Avg Return<br/>after resurface</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(r => {
                const exceededMax = r.exceeded_max;
                const isHotInYear = r.count_2026_at_ball > 0;
                const shadeNext = r.next_month_pct >= 4 ? 'bg-fuchsia-200 text-fuchsia-900 font-bold' :
                                  r.next_month_pct >= 2.5 ? 'bg-fuchsia-100 text-fuchsia-800' :
                                  r.next_month_pct >= 1 ? 'text-fuchsia-700' :
                                  'text-slate-400';
                // Highlight: top-2 distinct counts → green circle, zero → red circle
                const isTopHit = r.next_month_count > 0 && r.next_month_count >= top2Threshold && top2Threshold > 0;
                const isZeroHit = r.next_month_count === 0;
                return (
                  <tr key={r.number} className={`border-b border-slate-100 ${exceededMax ? 'bg-red-50' : 'hover:bg-slate-50'} ${tierBorder[r.hml] || ''}`} data-testid={`ms-row-${r.number}`}>
                    <td className="px-2 py-1.5 text-center font-mono font-black text-[#002FA7] sticky left-0 bg-white border-r border-slate-200">{r.number}</td>
                    {/* Till 2025 group */}
                    <td className="px-2 py-1.5 text-center font-mono text-blue-900">{r.till_2025_at_ball || '·'}</td>
                    <td className="px-2 py-1.5 text-center font-mono text-blue-700 font-bold">{r.avg_pr_yr_at_ball || '·'}</td>
                    <td className="px-2 py-1.5 text-center font-mono text-blue-900">{r.total_all_balls_till_2025}</td>
                    <td className="px-2 py-1.5 text-center font-mono text-blue-700 font-bold">{r.avg_pr_yr_all_balls}</td>
                    {/* All-balls May/next-month total — number's affinity to the month regardless of ball */}
                    <td className="px-2 py-1.5 text-center font-mono text-sky-800 font-bold bg-sky-50/40">{r.next_month_count_all_balls || '·'}</td>
                    {/* Next-draw month at this ball */}
                    <td className="px-2 py-1.5 text-center" data-testid={`ms-month-hits-${r.number}`}>
                      {isTopHit ? (
                        <span className="inline-flex w-9 h-9 rounded-full items-center justify-center bg-emerald-500 text-white font-mono font-black text-[12px] shadow-md ring-2 ring-emerald-200">
                          {r.next_month_count}
                        </span>
                      ) : isZeroHit ? (
                        <span className="inline-flex w-9 h-9 rounded-full items-center justify-center bg-red-500 text-white font-mono font-black text-[12px] shadow-md ring-2 ring-red-200">
                          0
                        </span>
                      ) : (
                        <span className="font-mono text-fuchsia-900 font-bold">{r.next_month_count}</span>
                      )}
                    </td>
                    <td className={`px-2 py-1.5 text-center font-mono ${shadeNext}`}>
                      {r.next_month_pct > 0 ? `${r.next_month_pct}%` : '·'}
                    </td>
                    {/* HML */}
                    <td className="px-2 py-1.5 text-center">
                      {r.hml ? (
                        <span className={`inline-block px-2 py-0.5 rounded font-black text-[10px] ${tierBg[r.hml]}`}>{r.hml}</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    {/* 2026 */}
                    <td className={`px-2 py-1.5 text-center font-mono font-bold ${isHotInYear ? 'text-amber-800 bg-amber-50' : 'text-slate-400'}`}>
                      {r.count_2026_at_ball || '·'}
                    </td>
                    {/* Gap */}
                    <td className="px-2 py-1.5 text-center font-mono text-rose-700 font-bold">{r.max_gap || '·'}</td>
                    <td className={`px-2 py-1.5 text-center font-mono font-bold ${exceededMax ? 'bg-red-200 text-red-900' : r.current_gap > r.max_gap * 0.7 ? 'text-amber-700' : 'text-rose-700'}`}>
                      {r.current_gap !== null ? r.current_gap : '·'}
                      {exceededMax && <span className="block text-[8px] font-black text-red-800">⚠ MAX</span>}
                    </td>
                    {/* Cycle phase */}
                    <td className="px-2 py-1.5 text-center font-mono text-indigo-700 text-[11px]">
                      <div className="font-bold">{r.avg_interval || '·'}</div>
                      <div className="text-[9px] text-slate-500">min {r.min_interval || '·'}</div>
                    </td>
                    <td className="px-2 py-1.5 text-center font-mono">
                      <div className={`font-bold ${r.cycle_phase > 120 ? 'text-rose-700' : r.cycle_phase > 80 ? 'text-amber-700' : 'text-slate-600'}`}>
                        {r.cycle_phase ? `${r.cycle_phase}%` : '·'}
                      </div>
                      {r.cycle_status && r.cycle_phase > 0 && (
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-black mt-0.5 ${
                          r.cycle_status === 'OVERDUE' ? 'bg-rose-200 text-rose-800' :
                          r.cycle_status === 'DUE' ? 'bg-amber-200 text-amber-800' :
                          'bg-slate-200 text-slate-700'
                        }`}>{r.cycle_status}</span>
                      )}
                    </td>
                    {/* Cross-ball */}
                    <td className="px-2 py-1.5 text-center font-mono text-violet-700">
                      {r.recent_surface_ball ? (
                        <span className="inline-block px-1.5 py-0.5 bg-violet-100 rounded text-[10px] font-bold">
                          {r.recent_surface_ball.replace('Ball ', 'B')}{r.recent_surface_tier ? `·${r.recent_surface_tier}` : ''}
                        </span>
                      ) : '·'}
                    </td>
                    <td className="px-2 py-1.5 text-center font-mono text-violet-700 font-bold">
                      {r.recent_surface_draws_ago !== null ? r.recent_surface_draws_ago : '·'}
                    </td>
                    <td className="px-2 py-1.5 text-center font-mono text-cyan-700 font-bold">
                      {r.avg_return_after_resurface !== null ? r.avg_return_after_resurface : '·'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-[11px] text-blue-800 leading-relaxed">
        <b>Column guide:</b>{' '}
        <b className="text-blue-700">Till 2025 {currentBall.label}</b> = appearances at this ball before 2026.{' '}
        <b className="text-blue-700">AVG /YR</b> = annual rate (Till-2025 ÷ {data.years_till_2025} yrs).{' '}
        <b className="text-blue-700">Total all balls till 2025</b> = lifetime hits across every ball before 2026.{' '}
        <b className="text-sky-700">{data.next_month_name} all balls till 2025</b> = number's <i>total</i> appearances in {data.next_month_name} across <b>every</b> ball position till 2025 (cross-ball month affinity).{' '}
        <b className="text-fuchsia-700">{data.next_month_name} hits / %</b> = number's affinity to the next-draw month <i>at this ball specifically</i>.{' '}
        <b className="text-emerald-700">●</b> green circle = top-2 hits for the next-draw month at this ball.{' '}
        <b className="text-red-600">●</b> red circle = zero hits in this month historically (cold candidates).{' '}
        <b className="text-slate-700">HML</b> = tier classification at this ball.{' '}
        <b className="text-amber-700">2026 {currentBall.label}</b> = hits at this ball in 2026.{' '}
        <b className="text-rose-700">Max GAP</b> = longest historical absence at this ball.{' '}
        <b className="text-rose-700">Current GAP</b> = draws since last appearance at this ball (red ⚠ MAX = exceeded record).{' '}
        <b className="text-indigo-700">Avg Int / min</b> = avg & min draws between consecutive appearances.{' '}
        <b className="text-indigo-700">Cycle %</b> = current_gap ÷ avg_interval × 100. Status: <span className="text-rose-700 font-bold">OVERDUE</span> &gt;120%, <span className="text-amber-700 font-bold">DUE</span> &gt;80%, NORMAL otherwise.{' '}
        <b className="text-violet-700">Surfaced @</b> = number's most recent appearance at a different ball during current absence.{' '}
        <b className="text-cyan-700">Avg Return</b> = historic mean draws to come back to this ball after surfacing elsewhere.
      </div>
    </div>
  );
}



export default function Window7NumberAppearance() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'times', dir: 'desc' });
  const [colWidths, setColWidths] = useState({ ...defaultWidths });
  const [mainView, setMainView] = useState('frequency');
  const handleResize = (key, w) => setColWidths(prev => ({ ...prev, [key]: w }));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analysis/number-appearance`);
      setData(res.data);
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useDrawsUpdated(fetchData);

  // Deep-link via ?view= query param (e.g. /window7?view=cycles from old /window30 redirect)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const view = params.get('view');
    const validViews = ['classification', 'frequency', 'patterns', 'position-hml', 'constraints', 'cycles', 'master'];
    if (view && validViews.includes(view)) {
      setMainView(view);
    }
  }, [location.search]);

  // Deep-link: if URL hash points to a block inside a specific view, switch view + scroll
  useEffect(() => {
    const hash = (window.location.hash || '').replace('#', '');
    if (!hash || !data) return;
    const viewForHash = {
      'block2-annual-tier': 'patterns',
    };
    const idForHash = {
      'block2-annual-tier': 'block2-annual-tier',
    };
    if (viewForHash[hash]) {
      setMainView(viewForHash[hash]);
      setTimeout(() => {
        const el = document.getElementById(idForHash[hash]);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          el.classList.add('ring-2', 'ring-amber-400', 'rounded-lg');
          setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400', 'rounded-lg'), 3500);
        }
      }, 600);
    }
  }, [data]);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const res = await axios.get(`${API}/analysis/number-appearance/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', `number_appearance_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success('Export completed');
    } catch { toast.error('Failed to export'); }
    finally { setExportLoading(false); }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  };

  const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-700" data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>Window 7</h1>
                <p className="text-xs text-gray-500">Number Appearance Frequency (H/M/L)</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => { setColWidths({ ...defaultWidths }); toast.success('Reset'); }} variant="ghost" size="sm" className="text-gray-500"><ArrowsOutLineHorizontal size={18} /></Button>
              <Button onClick={handleExport} disabled={exportLoading} className="btn-secondary flex items-center gap-2" data-testid="export-btn">
                {exportLoading ? <CircleNotch size={18} className="animate-spin" /> : <DownloadSimple size={18} />}
                Export Excel
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">


        <WindowNarrative windowId="W7" {...NARRATIVES.W7} className="mb-5" />
        <PageConsensusBanner className="mb-4" />
        {data?.risk_flags && Object.keys(data.risk_flags).length > 0 && (
          <div className="mb-4">
            <BlindSpotsBanner
              riskFlags={data.risk_flags}
              topPicks={Object.keys(data.risk_flags).map(k => ({ id: k, tier: 'TOP' }))}
              itemLabel="numbers"
              renderItem={(id) => {
                const [bk, num] = String(id).split('|');
                const ballNum = bk.replace('ball_', '');
                return <span className="font-mono">B{ballNum}: {num}</span>;
              }}
            />
          </div>
        )}
        {/* View Toggle */}
        <div className="flex gap-2 mb-6 border-b border-gray-200 pb-0" data-testid="main-view-toggle">
          <button
            onClick={() => setMainView('classification')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-[1px] ${
              mainView === 'classification'
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
            }`}
            data-testid="view-classification"
          >
            <Star size={16} />
            HML Classification <span className="text-[10px] font-normal opacity-70">(4 blocks)</span>
          </button>
          <button
            onClick={() => setMainView('frequency')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-[1px] ${
              mainView === 'frequency'
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
            }`}
            data-testid="view-frequency"
          >
            <ListBullets size={16} />
            Number Frequency (H/M/L) <span className="text-[10px] font-normal opacity-70">(3 blocks per ball + 1 shared)</span>
          </button>
          <button
            onClick={() => setMainView('patterns')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-[1px] ${
              mainView === 'patterns'
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
            }`}
            data-testid="view-patterns"
          >
            <ChartBar size={16} />
            HML Draw Patterns <span className="text-[10px] font-normal opacity-70">(6 blocks)</span>
          </button>
          <button
            onClick={() => setMainView('position-hml')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-[1px] ${
              mainView === 'position-hml'
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
            }`}
            data-testid="view-position-hml"
          >
            <Target size={16} />
            Individual Ball Analysis <span className="text-[10px] font-normal opacity-70">(5 blocks)</span>
          </button>
          <button
            onClick={() => setMainView('constraints')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-[1px] ${
              mainView === 'constraints'
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
            }`}
            data-testid="view-constraints"
          >
            <ArrowsOutLineHorizontal size={16} />
            Set Constraints HML <span className="text-[10px] font-normal opacity-70">(1 block)</span>
          </button>
          <button
            onClick={() => setMainView('cycles')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-[1px] ${
              mainView === 'cycles'
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
            }`}
            data-testid="view-cycles"
          >
            <Clock size={16} />
            Cycle Phase <span className="text-[10px] font-normal opacity-70">(was W30)</span>
          </button>
          <button
            onClick={() => setMainView('master')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-[1px] ${
              mainView === 'master'
                ? 'border-[#002FA7] text-[#002FA7]'
                : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-300'
            }`}
            data-testid="view-master"
          >
            <ListBullets size={16} />
            Master Sheet <span className="text-[10px] font-normal opacity-70">(per-ball, 50 nums)</span>
          </button>
        </div>

        {mainView === 'classification' ? (
          <ClassificationTab />
        ) : mainView === 'constraints' ? (
          <ConstraintsTab />
        ) : mainView === 'position-hml' ? (
          <PositionHMLTab />
        ) : mainView === 'patterns' ? (
          <HMLPatternsTab />
        ) : mainView === 'cycles' ? (
          <CyclePhaseTab />
        ) : mainView === 'master' ? (
          <MasterSheetTab />
        ) : (
        <>
        {loading ? (
          <div className="flex items-center justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>
        ) : data ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Total Draws</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{data.total_draws}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Years Covered</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{data.num_years}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4 bg-amber-50 border-amber-200">
                <p className="text-xs uppercase tracking-wider font-bold text-amber-600">{data.current_month_name} Draws</p>
                <p className="text-2xl font-bold text-amber-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{data.total_month_draws}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4 bg-blue-50 border-blue-200">
                <p className="text-xs uppercase tracking-wider font-bold text-blue-600">Classification</p>
                <p className="text-sm font-bold text-blue-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>
                  <span className="text-green-700">H</span>=High <span className="text-amber-700">M</span>=Medium <span className="text-red-700">L</span>=Low
                </p>
              </div>
            </div>

            <div className="text-xs text-gray-400 mb-2 flex items-center gap-1">
              <ArrowsOutLineHorizontal size={14} />
              <span>Drag column borders to resize | <strong>%</strong> = overall freq | <strong>{data.current_month_abbr}</strong> = {data.current_month_name}-specific | <strong>%Diff</strong> = {data.current_year}% - Overall% | <strong>AvgDf</strong> = Overall avg/yr - Recent 3yr avg/yr</span>
            </div>

            <Tabs defaultValue="ball_1" className="space-y-4">
              <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
                {Object.entries(ballLabels).map(([key, label]) => (
                  <TabsTrigger key={key} value={key} className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid={`tab-${key}`}>
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {Object.entries(ballLabels).map(([key, label]) => {
                return (
                  <TabsContent key={key} value={key}>
                    <div className="bg-white border border-amber-200 rounded-lg p-5 text-center" data-testid={`freq-removed-notice-${key}`}>
                      <div className="text-amber-700 font-bold text-sm mb-1">Blocks 1, 2 & 3 retired</div>
                      <p className="text-[12px] text-gray-600">
                        Number Appearance Frequency, Most Probable Numbers, and HML Frequency Summary have been moved to the new <b>Master Sheet</b> tab — which auto-updates with the latest draw and combines all of these signals into a single per-ball matrix.
                      </p>
                    </div>
                  </TabsContent>
                );
              })}
            </Tabs>

            {/* 2026 vs Pre-2026 Per-Ball Frequency Diff — moved from W3 */}
            {data.per_ball_number_frequency_diff_2026 && (() => {
              const p = data.per_ball_number_frequency_diff_2026;
              const rows = [...p.rows].sort((a, b) => Math.abs(b.total_diff) - Math.abs(a.total_diff));
              const cellClass = (d) => {
                if (d > 1.5) return 'bg-emerald-600 text-white font-bold';
                if (d > 0.5) return 'bg-emerald-200 text-emerald-900 font-bold';
                if (d > 0) return 'bg-emerald-50 text-emerald-700';
                if (d < -1.5) return 'bg-rose-600 text-white font-bold';
                if (d < -0.5) return 'bg-rose-200 text-rose-900 font-bold';
                if (d < 0) return 'bg-rose-50 text-rose-700';
                return 'bg-gray-50 text-gray-400';
              };
              return (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mt-6" data-testid="per-ball-freq-diff-2026">
                  <div className="bg-gradient-to-r from-indigo-700 to-purple-700 text-white px-4 py-3">
                    <h4 className="font-bold">Shared Block — Per-Ball Number Frequency Diff — 2026 vs Pre-2026 <span className="text-[10px] font-normal opacity-80">(same table on every ball tab)</span></h4>
                    <p className="text-[11px] text-white/80 mt-0.5">
                      For each number & position: <span className="font-mono font-bold">diff = actual_2026 − expected_2026</span> where expected = pre-2026 count × ({p.draws_2026} ÷ {p.draws_pre_2026} = <span className="font-mono font-bold">{p.scale}</span>). Positive (green) = over-appearing in 2026 vs historical baseline.
                    </p>
                  </div>
                  <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-200 flex items-center gap-4 flex-wrap text-[11px]">
                    <span className="font-bold text-indigo-800">2026 draws: <span className="font-mono">{p.draws_2026}</span></span>
                    <span className="font-bold text-indigo-800">Pre-2026 draws: <span className="font-mono">{p.draws_pre_2026}</span></span>
                    <span className="font-bold text-indigo-800">Scale: <span className="font-mono">{p.scale}</span></span>
                    <span className="ml-auto text-indigo-600 italic">Sorted by absolute |total_diff| desc (most-shifted numbers first)</span>
                  </div>
                  <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                    <table className="w-full text-[11px]">
                      <thead className="bg-gray-100 sticky top-0 z-10">
                        <tr>
                          <th className="px-2 py-2 text-center font-bold text-gray-600 border">#</th>
                          <th className="px-2 py-2 text-center font-bold text-gray-600 border" title="2026 count across all 5 balls">Σ 2026</th>
                          <th className="px-2 py-2 text-center font-bold text-gray-600 border" title="Pre-2026 count across all 5 balls">Σ Pre</th>
                          {[1,2,3,4,5].map(i => (
                            <th key={i} colSpan={4} className="px-2 py-1.5 text-center font-bold text-gray-700 border bg-indigo-50">Ball {i}</th>
                          ))}
                          <th className="px-2 py-2 text-center font-bold text-gray-600 border bg-amber-50" title="Sum of diff across all 5 balls">Total Diff</th>
                        </tr>
                        <tr className="bg-gray-50">
                          <th className="px-2 py-1 border"></th>
                          <th className="px-2 py-1 border"></th>
                          <th className="px-2 py-1 border"></th>
                          {[1,2,3,4,5].map(i => (
                            <React.Fragment key={i}>
                              <th className="px-1 py-1 text-center text-[9px] text-gray-500 border bg-indigo-50/50">2026</th>
                              <th className="px-1 py-1 text-center text-[9px] text-gray-500 border bg-indigo-50/50">Pre</th>
                              <th className="px-1 py-1 text-center text-[9px] text-gray-500 border bg-indigo-50/50">Exp</th>
                              <th className="px-1 py-1 text-center text-[9px] text-gray-500 border bg-indigo-50/50">Diff</th>
                            </React.Fragment>
                          ))}
                          <th className="px-2 py-1 border"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, idx) => (
                          <tr key={r.number} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} data-testid={`w7-freq-diff-row-${r.number}`}>
                            <td className="px-2 py-1 text-center font-mono font-bold text-gray-800 border">{r.number}</td>
                            <td className="px-2 py-1 text-center font-mono font-bold text-indigo-700 border">{r.total_actual}</td>
                            <td className="px-2 py-1 text-center font-mono text-gray-500 border">{r.total_pre}</td>
                            {r.per_ball.map((b, i) => (
                              <React.Fragment key={i}>
                                <td className="px-1 py-1 text-center font-mono text-gray-700 border">{b.actual}</td>
                                <td className="px-1 py-1 text-center font-mono text-gray-400 border">{b.pre}</td>
                                <td className="px-1 py-1 text-center font-mono text-gray-500 border">{b.expected}</td>
                                <td className={`px-1 py-1 text-center font-mono border ${cellClass(b.diff)}`}>{b.diff > 0 ? `+${b.diff}` : b.diff}</td>
                              </React.Fragment>
                            ))}
                            <td className={`px-2 py-1 text-center font-mono font-bold border ${cellClass(r.total_diff)}`}>{r.total_diff > 0 ? `+${r.total_diff}` : r.total_diff}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-4 py-2 bg-gray-50 border-t text-[11px] text-gray-600 flex items-center gap-4 flex-wrap">
                    <span className="font-bold">Legend:</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-600" /> diff &gt; +1.5 (strongly over-appearing)</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-200" /> +0.5 to +1.5</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-200" /> −1.5 to −0.5</span>
                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-600" /> diff &lt; −1.5 (strongly under-appearing)</span>
                  </div>
                </div>
              );
            })()}
          </>
        ) : null}
        </>
        )}
      </main>
    </div>
  );
}
